import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { Input, InputNumber, Modal, notification } from "antd";
import axios from "axios";
import { useAuth } from "../../services/AuthContext";
import { isOwnerAccount } from "../../utils/ownerAccess";
import {
  IoCartOutline,
  IoCheckmarkCircleOutline,
  IoChevronForward,
  IoDiamondOutline,
  IoFilterOutline,
  IoLockClosedOutline,
  IoPeopleOutline,
  IoRefreshOutline,
  IoRocketOutline,
  IoTimeOutline,
  IoTrophyOutline,
} from "react-icons/io5";
import { getLocaleForLanguage, normalizeLanguage } from "../../services/language";
import { t as i18nT } from "../../i18n";
import { useInterfaceLanguage } from "../../hooks/useInterfaceLanguage";


const MODE_OPTIONS = [
  {
    id: "free",
    icon: "/mode-icons/free.png",
    cardClass: "xima-play-mode-science",
    thumbnail: "/assets/images/fotociencia.png",
  },
  {
    id: "dice",
    icon: "/mode-icons/logo-modo-azar.png",
    cardClass: "xima-play-mode-luck",
    thumbnail: "/assets/images/fotoazar.png",
  },
  {
    id: "cienzar",
    icon: "/mode-icons/cienzar.png",
    cardClass: "xima-play-mode-cienzar",
    thumbnail: "/assets/images/fotocienzar.png",
  },
  {
    id: "sciencevsazar",
    icon: "/mode-icons/logo-contrazar.png",
    cardClass: "xima-play-mode-sciencevsazar",
    thumbnail: "/assets/images/fotocienzar.png",
  },
];

const MODE_LABELS = {
  free: "Modo Ciencia",
  dice: "Modo Azar",
  cienzar: "Modo Cienzar",
  sciencevsazar: "Modo Ciencia V/S Azar",
};

const MODE_THEME_BY_ID = {
  free: {
    label: MODE_LABELS.free,
    icon: "/mode-icons/free.png",
    colorClass: "science",
    thumbnail: "/assets/images/fotociencia.png",
  },
  dice: {
    label: MODE_LABELS.dice,
    icon: "/mode-icons/logo-modo-azar.png",
    colorClass: "luck",
    thumbnail: "/assets/images/fotoazar.png",
  },
  cienzar: {
    label: MODE_LABELS.cienzar,
    icon: "/mode-icons/cienzar.png",
    colorClass: "cienzar",
    thumbnail: "/assets/images/fotocienzar.png",
  },
  sciencevsazar: {
    label: MODE_LABELS.sciencevsazar,
    icon: "/mode-icons/logo-contrazar.png",
    colorClass: "sciencevsazar",
    thumbnail: "/assets/images/fotocienzar.png",
  },
};

const DEFAULT_PLAYER_FILTER_VALUES = [2, 3, 4];
const DEFAULT_TIME_FILTER_VALUES = [1, 3, 5, 10, 15];
const LAST_JOINED_TOURNAMENT_PREFIX = "xima_last_joined_tournament_v1_";
const DEFAULT_TURN_TIME = 5;
const MEMBER_OPTIONS = [2, 3, 4];
const TOURNAMENT_EDIT_HAVING_TIME_OPTIONS = [1, 2, 3, 5, 10, 15, 30, 60];
const TOURNAMENT_DEFAULT_REWARD_VALUE = 0;
const MAX_TOURNAMENT_REWARD_VALUE = 1_000_000_000;
const MATCH_PASSWORD_MIN_LENGTH = 1;
const MATCH_PASSWORD_MAX_LENGTH = 4;
const MATCH_PASSWORD_REGEX = /^\d+$/;
const TOURNAMENT_BADGE_IMAGE_CACHE = new Map();
const TOURNAMENT_TIME_BADGE_IMAGE_BY_MINUTES = {
  1: "/assets/images/1minutomorado.svg",
  2: "/assets/images/2minutosmorados.svg",
  5: "/assets/images/5minutosmorados.png",
  10: "/assets/images/10minutosmorados.png",
  30: "/assets/images/30minutosmorados.png",
};
const TOURNAMENT_PLAYERS_BADGE_IMAGE_BY_SIZE = {
  2: "/assets/images/players-1v1.png",
  3: "/assets/images/players-1v3.png",
  4: "/assets/images/players-1-5.png",
};

const getCachedTournamentBadgeImage = (key, buildSvg) => {
  if (TOURNAMENT_BADGE_IMAGE_CACHE.has(key)) {
    return TOURNAMENT_BADGE_IMAGE_CACHE.get(key);
  }
  const src = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(buildSvg())}`;
  TOURNAMENT_BADGE_IMAGE_CACHE.set(key, src);
  return src;
};

const normalizeTournamentBadgeNumber = (value, fallback = 1) => {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getTournamentPlayersBadgeImage = (players) => {
  const count = normalizeTournamentBadgeNumber(players, 2);
  const knownBadge =
    TOURNAMENT_PLAYERS_BADGE_IMAGE_BY_SIZE[count] ||
    TOURNAMENT_PLAYERS_BADGE_IMAGE_BY_SIZE[Math.min(4, Math.max(2, count))];
  if (knownBadge) return knownBadge;
  const countLabel = count > 99 ? "99+" : String(count);
  return getCachedTournamentBadgeImage(`players-${countLabel}`, () => `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
      <defs>
        <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stop-color="#103d7a"/>
          <stop offset="0.56" stop-color="#071d3e"/>
          <stop offset="1" stop-color="#020712"/>
        </linearGradient>
        <linearGradient id="gold" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stop-color="#fff3a4"/>
          <stop offset="0.48" stop-color="#ffc13f"/>
          <stop offset="1" stop-color="#b66b08"/>
        </linearGradient>
      </defs>
      <rect x="6" y="6" width="116" height="116" rx="24" fill="url(#bg)" stroke="url(#gold)" stroke-width="5"/>
      <circle cx="47" cy="40" r="15" fill="#7ed7ff"/>
      <circle cx="81" cy="40" r="15" fill="#d9f3ff"/>
      <path d="M22 80c5-22 19-33 41-33s36 11 43 33" fill="none" stroke="#d9f3ff" stroke-width="13" stroke-linecap="round"/>
      <path d="M26 83c4-17 15-26 32-26s29 9 35 26" fill="none" stroke="#55bdff" stroke-width="12" stroke-linecap="round"/>
      <text x="64" y="101" text-anchor="middle" fill="#fff7d2" font-family="Arial, sans-serif" font-size="34" font-weight="900">${countLabel}</text>
      <text x="64" y="116" text-anchor="middle" fill="#ffc13f" font-family="Arial, sans-serif" font-size="12" font-weight="900" letter-spacing="1">JUG</text>
    </svg>
  `);
};

const getTournamentTimeBadgeImage = (minutes) => {
  const value = normalizeTournamentBadgeNumber(minutes, DEFAULT_TURN_TIME);
  if (TOURNAMENT_TIME_BADGE_IMAGE_BY_MINUTES[value]) {
    return TOURNAMENT_TIME_BADGE_IMAGE_BY_MINUTES[value];
  }
  const minuteLabel = value > 99 ? "99+" : String(value);
  return getCachedTournamentBadgeImage(`time-${minuteLabel}`, () => `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
      <defs>
        <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stop-color="#3b1b69"/>
          <stop offset="0.52" stop-color="#1d0c3f"/>
          <stop offset="1" stop-color="#080410"/>
        </linearGradient>
        <linearGradient id="gold" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stop-color="#fff3a4"/>
          <stop offset="0.48" stop-color="#ffc13f"/>
          <stop offset="1" stop-color="#b66b08"/>
        </linearGradient>
      </defs>
      <rect x="6" y="6" width="116" height="116" rx="24" fill="url(#bg)" stroke="url(#gold)" stroke-width="5"/>
      <circle cx="64" cy="47" r="29" fill="none" stroke="#fff7d2" stroke-width="8"/>
      <path d="M64 29v20l15 10" fill="none" stroke="#ffc13f" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="64" cy="47" r="5" fill="#ffc13f"/>
      <text x="64" y="100" text-anchor="middle" fill="#fff7d2" font-family="Arial, sans-serif" font-size="33" font-weight="900">${minuteLabel}</text>
      <text x="64" y="116" text-anchor="middle" fill="#ffc13f" font-family="Arial, sans-serif" font-size="12" font-weight="900" letter-spacing="1">MIN</text>
    </svg>
  `);
};

const normalizeMatchPassword = (value) =>
  String(value || "")
    .replace(/[^\d]/g, "")
    .slice(0, MATCH_PASSWORD_MAX_LENGTH);

const isValidMatchPassword = (value) => {
  const normalizedPassword = normalizeMatchPassword(value);
  return (
    normalizedPassword.length >= MATCH_PASSWORD_MIN_LENGTH &&
    normalizedPassword.length <= MATCH_PASSWORD_MAX_LENGTH &&
    MATCH_PASSWORD_REGEX.test(normalizedPassword)
  );
};

const MODE_INTERFACE_SOURCE_BY_ID = {
  free: "modo-ciencia",
  dice: "modo-azar",
  cienzar: "modo-cienzar",
  sciencevsazar: "modo-ciencia-vs-azar",
};

const MODE_INTERFACE_LOBBY_SOURCE_BY_ID = {
  free: "lobby-science-mode",
  dice: "lobby-luck-mode",
  cienzar: "lobby-cienzar-mode",
  sciencevsazar: "lobby-science-vs-azar-mode",
};

const TOURNAMENT_COPY = Object.freeze({
  backToLobby: {
    es: "Volver al lobby",
    en: "Back to lobby",
    de: "Zurück zur Lobby",
    ru: "Назад в лобби",
  },
  playTitle: {
    es: "Jugar",
    en: "Play",
    de: "Spielen",
    ru: "Играть",
  },
  playSubtitle: {
    es: "Elige tu modo favorito y juega en un torneo en vivo.",
    en: "Choose your favorite mode and play in a live tournament.",
    de: "Wähle deinen Lieblingsmodus und spiele in einem Live-Turnier.",
    ru: "Выбери любимый режим и играй в живом турнире.",
  },
  all: {
    es: "Todos",
    en: "All",
    de: "Alle",
    ru: "Все",
  },
  allModes: {
    es: "Todos los modos",
    en: "All modes",
    de: "Alle Modi",
    ru: "Все режимы",
  },
  openTournaments: {
    es: "Torneos abiertos",
    en: "Open tournaments",
    de: "Offene Turniere",
    ru: "Открытые турниры",
  },
  openTournamentsDescription: {
    es: "Únete a torneos oficiales en vivo y demuestra tu habilidad",
    en: "Join official live tournaments and prove your skill",
    de: "Tritt offiziellen Live-Turnieren bei und beweise dein Können",
    ru: "Присоединяйся к официальным живым турнирам и докажи своё мастерство",
  },
  editTournament: {
    es: "Editar torneo",
    en: "Edit tournament",
    de: "Turnier bearbeiten",
    ru: "Редактировать турнир",
  },
  editTournamentOn: {
    es: "Editar torneo on",
    en: "Edit tournament on",
    de: "Turnier bearbeiten ein",
    ru: "Редактирование турнира включено",
  },
  mode: {
    es: "Modo",
    en: "Mode",
    de: "Modus",
    ru: "Режим",
  },
  filterByMode: {
    es: "Filtrar por modo",
    en: "Filter by mode",
    de: "Nach Modus filtern",
    ru: "Фильтр по режиму",
  },
  filterPlayers: {
    es: "Filtra jugadores",
    en: "Filter players",
    de: "Spieler filtern",
    ru: "Фильтр игроков",
  },
  filterPlayersDescription: {
    es: "Elige la cantidad total de jugadores del torneo.",
    en: "Choose the total number of players in the tournament.",
    de: "Wähle die Gesamtzahl der Spieler im Turnier.",
    ru: "Выбери общее количество игроков в турнире.",
  },
  filterPlayersShortDescription: {
    es: "Cantidad total del torneo",
    en: "Total players in the tournament",
    de: "Gesamtzahl der Spieler",
    ru: "Всего игроков в турнире",
  },
  filterTime: {
    es: "Filtra tiempo",
    en: "Filter time",
    de: "Zeit filtern",
    ru: "Фильтр времени",
  },
  filterTimeDescription: {
    es: "Filtra por minutos de juego por turno.",
    en: "Filter by minutes of game time per turn.",
    de: "Filtere nach Spielminuten pro Zug.",
    ru: "Фильтруй по количеству минут на ход.",
  },
  filterTimeShortDescription: {
    es: "Minutos por turno",
    en: "Minutes per turn",
    de: "Minuten pro Zug",
    ru: "Минут на ход",
  },
  activeTournamentDescription: {
    es: "Torneo activo disponible para unirse.",
    en: "Active tournament available to join.",
    de: "Aktives Turnier verfügbar zum Beitreten.",
    ru: "Активный турнир доступен для входа.",
  },
  scheduledTournamentDescription: {
    es: "Torneo programado para iniciar pronto.",
    en: "Tournament scheduled to start soon.",
    de: "Turnier ist geplant und startet bald.",
    ru: "Турнир запланирован и скоро начнётся.",
  },
  registeredTournamentDescription: {
    es: "Ya estás inscrito. Entra directo a tu torneo.",
    en: "You are already registered. Enter your tournament directly.",
    de: "Du bist bereits angemeldet. Tritt direkt deinem Turnier bei.",
    ru: "Вы уже зарегистрированы. Войдите прямо в свой турнир.",
  },
  active: {
    es: "Activo",
    en: "Active",
    de: "Aktiv",
    ru: "Активен",
  },
  startingSoon: {
    es: "Por empezar",
    en: "Starting soon",
    de: "Startet bald",
    ru: "Скоро начнётся",
  },
  starts: {
    es: "Empieza:",
    en: "Starts:",
    de: "Beginnt:",
    ru: "Начинается:",
  },
  ends: {
    es: "Termina:",
    en: "Ends:",
    de: "Endet:",
    ru: "Заканчивается:",
  },
  joined: {
    es: "Unidos:",
    en: "Joined:",
    de: "Beigetreten:",
    ru: "Присоединились:",
  },
  withPassword: {
    es: "Con contraseña",
    en: "With password",
    de: "Mit Passwort",
    ru: "С паролем",
  },
  passwordTitle: {
    es: "Contraseña {value}",
    en: "Password {value}",
    de: "Passwort {value}",
    ru: "Пароль {value}",
  },
  passwordPrizePaymentTitle: {
    es: "Contraseña, premio y pago",
    en: "Password, prize and payment",
    de: "Passwort, Preis und Zahlung",
    ru: "Пароль, приз и оплата",
  },
  withAndWithoutChancePlayersTitle: {
    es: "Con y sin azar {players}",
    en: "With and without chance {players}",
    de: "Mit und ohne Zufall {players}",
    ru: "Со случайностью и без неё, {players}",
  },
  withAndWithoutDicePlayersTitle: {
    es: "Con y sin dados {players}",
    en: "With and without dice {players}",
    de: "Mit und ohne Würfel {players}",
    ru: "С кубиками и без них, {players}",
  },
  payment: {
    es: "Pago {value}X",
    en: "Payment {value}X",
    de: "Zahlung {value}X",
    ru: "Оплата {value}X",
  },
  prize: {
    es: "Premio",
    en: "Prize",
    de: "Preis",
    ru: "Приз",
  },
  join: {
    es: "Unirse",
    en: "Join",
    de: "Beitreten",
    ru: "Присоединиться",
  },
  enter: {
    es: "Entrar",
    en: "Enter",
    de: "Eintreten",
    ru: "Войти",
  },
  opening: {
    es: "Abriendo...",
    en: "Opening...",
    de: "Wird geöffnet...",
    ru: "Открытие...",
  },
  open: {
    es: "Abrir",
    en: "Open",
    de: "Öffnen",
    ru: "Открыть",
  },
  edit: {
    es: "Editar",
    en: "Edit",
    de: "Bearbeiten",
    ru: "Редактировать",
  },
  registered: {
    es: "Inscrito",
    en: "Registered",
    de: "Angemeldet",
    ru: "Зарегистрирован",
  },
  loadingTournaments: {
    es: "Cargando torneos...",
    en: "Loading tournaments...",
    de: "Turniere werden geladen...",
    ru: "Загрузка турниров...",
  },
  noOpenTournaments: {
    es: "No hay torneos abiertos para este filtro.",
    en: "There are no open tournaments for this filter.",
    de: "Für diesen Filter gibt es keine offenen Turniere.",
    ru: "Для этого фильтра нет открытых турниров.",
  },
  noMoreOpenTournaments: {
    es: "No hay más torneos abiertos para este filtro.",
    en: "There are no more open tournaments for this filter.",
    de: "Für diesen Filter gibt es keine weiteren offenen Turniere.",
    ru: "Для этого фильтра больше нет открытых турниров.",
  },
  howItWorks: {
    es: "Cómo funciona",
    en: "How it works",
    de: "So funktioniert es",
    ru: "Как это работает",
  },
  chooseMode: {
    es: "Elige un modo",
    en: "Choose a mode",
    de: "Wähle einen Modus",
    ru: "Выбери режим",
  },
  chooseModeDescription: {
    es: "Selecciona tu estilo de juego favorito.",
    en: "Select your favorite play style.",
    de: "Wähle deinen bevorzugten Spielstil.",
    ru: "Выбери любимый стиль игры.",
  },
  joinTournamentGuide: {
    es: "Únete a un torneo",
    en: "Join a tournament",
    de: "Tritt einem Turnier bei",
    ru: "Присоединись к турниру",
  },
  joinTournamentGuideDescription: {
    es: "Elige un torneo abierto de la lista.",
    en: "Choose an open tournament from the list.",
    de: "Wähle ein offenes Turnier aus der Liste.",
    ru: "Выбери открытый турнир из списка.",
  },
  competeAndWin: {
    es: "Compite y gana",
    en: "Compete and win",
    de: "Kämpfe und gewinne",
    ru: "Соревнуйся и побеждай",
  },
  competeAndWinDescription: {
    es: "Demuestra tu habilidad y gana premios.",
    en: "Show your skill and win prizes.",
    de: "Beweise dein Können und gewinne Preise.",
    ru: "Докажи своё мастерство и выигрывай призы.",
  },
  topPlayers: {
    es: "Mejores jugadores",
    en: "Top players",
    de: "Beste Spieler",
    ru: "Лучшие игроки",
  },
  viewRankingAria: {
    es: "Ver ranking",
    en: "View ranking",
    de: "Rangliste anzeigen",
    ru: "Посмотреть рейтинг",
  },
  noRankingAvailable: {
    es: "Sin ranking disponible.",
    en: "No ranking available.",
    de: "Keine Rangliste verfügbar.",
    ru: "Рейтинг недоступен.",
  },
  viewFullRanking: {
    es: "Ver ranking completo",
    en: "View full ranking",
    de: "Vollständige Rangliste anzeigen",
    ru: "Посмотреть полный рейтинг",
  },
  goToShop: {
    es: "Ir a la tienda",
    en: "Go to shop",
    de: "Zum Shop",
    ru: "В магазин",
  },
  shopDescription: {
    es: "Consigue la nueva edición y mejoras exclusivas",
    en: "Get the new edition and exclusive upgrades",
    de: "Hol dir die neue Edition und exklusive Verbesserungen",
    ru: "Получи новую версию и эксклюзивные улучшения",
  },
  championshipTitle: {
    es: "Campeonato Mundial XIMA 2026",
    en: "XIMA World Championship 2026",
    de: "XIMA-Weltmeisterschaft 2026",
    ru: "Чемпионат мира по XIMA 2026",
  },
  championshipDescription: {
    es: "Sé parte del torneo más grande y demuestra tu habilidad.",
    en: "Be part of the biggest tournament and prove your skill.",
    de: "Sei Teil des größten Turniers und beweise dein Können.",
    ru: "Стань частью самого большого турнира и докажи своё мастерство.",
  },
  noDicePlayersTitle: {
    es: "Sin dados {players}",
    en: "No dice {players}",
    de: "Ohne Würfel {players}",
    ru: "Без кубиков {players}",
  },
  tournamentModeName: {
    es: "Torneo {mode}",
    en: "{mode} tournament",
    de: "{mode}-Turnier",
    ru: "Турнир {mode}",
  },
});

const MODE_INTERFACE_COPY_BY_ID = {
  free: {
    shortLabel: {
      es: "Ciencia",
      en: "Science",
      de: "Wissenschaft",
      ru: "Наука",
    },
    routeLabel: {
      es: "Modo Ciencia",
      en: "Science mode",
      de: "Wissenschaftsmodus",
      ru: "Режим «Наука»",
    },
    cardSubtitle: {
      es: "La estrategia lo es todo.",
      en: "Strategy is everything.",
      de: "Strategie ist alles.",
      ru: "Стратегия решает всё.",
    },
    cardDetail: {
      es: "Cada movimiento cuenta.",
      en: "Every move counts.",
      de: "Jeder Zug zählt.",
      ru: "Каждый ход имеет значение.",
    },
    intro: {
      es: "Elige un torneo Ciencia y filtra por jugadores o tiempo de juego.",
      en: "Choose a Science tournament and filter by players or game time.",
      de: "Wähle ein Wissenschaftsturnier und filtere nach Spielern oder Spielzeit.",
      ru: "Выбери турнир в режиме «Наука» и фильтруй по игрокам или времени игры.",
    },
    onlyLabel: {
      es: "Solo torneos oficiales Ciencia.",
      en: "Only official Science tournaments.",
      de: "Nur offizielle Wissenschaftsturniere.",
      ru: "Только официальные турниры режима «Наука».",
    },
    detail: {
      es: "Estrategia pura en vivo.",
      en: "Pure strategy live.",
      de: "Reine Strategie live.",
      ru: "Чистая стратегия в прямом эфире.",
    },
    empty: {
      es: "No hay torneos de Modo Ciencia abiertos para estos filtros.",
      en: "There are no open Science Mode tournaments for these filters.",
      de: "Für diese Filter gibt es keine offenen Turniere im Wissenschaftsmodus.",
      ru: "Для этих фильтров нет открытых турниров режима «Наука».",
    },
    panelDescription: {
      es: "Torneos oficiales del Modo Ciencia.",
      en: "Official Science Mode tournaments.",
      de: "Offizielle Turniere im Wissenschaftsmodus.",
      ru: "Официальные турниры режима «Наука».",
    },
    guideMode: {
      es: "Solo aparecen torneos oficiales de estrategia.",
      en: "Only official strategy tournaments appear.",
      de: "Es erscheinen nur offizielle Strategieturniere.",
      ru: "Показываются только официальные стратегические турниры.",
    },
  },
  dice: {
    shortLabel: {
      es: "Azar",
      en: "Chance",
      de: "Zufall",
      ru: "Случайность",
    },
    routeLabel: {
      es: "Modo Azar",
      en: "Chance mode",
      de: "Zufallsmodus",
      ru: "Режим случайности",
    },
    cardSubtitle: {
      es: "La suerte decide tu camino.",
      en: "Luck decides your path.",
      de: "Das Glück bestimmt deinen Weg.",
      ru: "Удача выбирает твой путь.",
    },
    cardDetail: {
      es: "¿Te atreves a confiar?",
      en: "Do you dare to trust it?",
      de: "Wagst du es, ihm zu vertrauen?",
      ru: "Осмелишься ей довериться?",
    },
    intro: {
      es: "Elige un torneo Azar y filtra por jugadores o tiempo de juego.",
      en: "Choose a Chance tournament and filter by players or game time.",
      de: "Wähle ein Zufallsturnier und filtere nach Spielern oder Spielzeit.",
      ru: "Выбери турнир в режиме случайности и фильтруй по игрокам или времени игры.",
    },
    onlyLabel: {
      es: "Solo torneos oficiales Azar.",
      en: "Only official Chance tournaments.",
      de: "Nur offizielle Zufallsturniere.",
      ru: "Только официальные турниры режима случайности.",
    },
    detail: {
      es: "La suerte también compite.",
      en: "Luck also competes.",
      de: "Das Glück spielt mit.",
      ru: "Удача тоже соревнуется.",
    },
    empty: {
      es: "No hay torneos de Modo Azar abiertos para estos filtros.",
      en: "There are no open Chance Mode tournaments for these filters.",
      de: "Für diese Filter gibt es keine offenen Turniere im Zufallsmodus.",
      ru: "Для этих фильтров нет открытых турниров режима случайности.",
    },
    panelDescription: {
      es: "Torneos oficiales del Modo Azar.",
      en: "Official Chance Mode tournaments.",
      de: "Offizielle Turniere im Zufallsmodus.",
      ru: "Официальные турниры режима случайности.",
    },
    guideMode: {
      es: "Solo aparecen torneos oficiales de Azar.",
      en: "Only official Chance tournaments appear.",
      de: "Es erscheinen nur offizielle Zufallsturniere.",
      ru: "Показываются только официальные турниры режима случайности.",
    },
  },
  cienzar: {
    shortLabel: {
      es: "CIENZAR",
      en: "CIENZAR",
      de: "CIENZAR",
      ru: "CIENZAR",
    },
    routeLabel: {
      es: "Modo Cienzar",
      en: "Cienzar mode",
      de: "Cienzar-Modus",
      ru: "Режим CIENZAR",
    },
    cardSubtitle: {
      es: "La combinación perfecta entre estrategia y suerte.",
      en: "The perfect combination of strategy and luck.",
      de: "Die perfekte Kombination aus Strategie und Glück.",
      ru: "Идеальное сочетание стратегии и удачи.",
    },
    cardDetail: {
      es: "",
      en: "",
      de: "",
      ru: "",
    },
    intro: {
      es: "Elige un torneo Cienzar y filtra por jugadores o tiempo de juego.",
      en: "Choose a Cienzar tournament and filter by players or game time.",
      de: "Wähle ein Cienzar-Turnier und filtere nach Spielern oder Spielzeit.",
      ru: "Выбери турнир Cienzar и фильтруй по игрокам или времени игры.",
    },
    onlyLabel: {
      es: "Solo torneos oficiales Cienzar.",
      en: "Only official Cienzar tournaments.",
      de: "Nur offizielle Cienzar-Turniere.",
      ru: "Только официальные турниры Cienzar.",
    },
    detail: {
      es: "Estrategia y suerte en equilibrio.",
      en: "Strategy and luck in balance.",
      de: "Strategie und Glück im Gleichgewicht.",
      ru: "Стратегия и удача в равновесии.",
    },
    empty: {
      es: "No hay torneos de Modo Cienzar abiertos para estos filtros.",
      en: "There are no open Cienzar Mode tournaments for these filters.",
      de: "Für diese Filter gibt es keine offenen Cienzar-Turniere.",
      ru: "Для этих фильтров нет открытых турниров Cienzar.",
    },
    panelDescription: {
      es: "Torneos oficiales del Modo Cienzar.",
      en: "Official Cienzar Mode tournaments.",
      de: "Offizielle Turniere im Cienzar-Modus.",
      ru: "Официальные турниры режима Cienzar.",
    },
    guideMode: {
      es: "Solo aparecen torneos oficiales de Cienzar.",
      en: "Only official Cienzar tournaments appear.",
      de: "Es erscheinen nur offizielle Cienzar-Turniere.",
      ru: "Показываются только официальные турниры Cienzar.",
    },
  },
  sciencevsazar: {
    shortLabel: {
      es: "CIENCIA V/S AZAR",
      en: "SCIENCE V/S CHANCE",
      de: "WISSENSCHAFT V/S ZUFALL",
      ru: "SCIENCE V/S CHANCE",
    },
    routeLabel: {
      es: "Modo Ciencia V/S Azar",
      en: "Science V/S Chance mode",
      de: "Wissenschaft V/S Zufall-Modus",
      ru: "Science V/S Chance mode",
    },
    cardSubtitle: {
      es: "Un bando juega ciencia; el otro juega azar.",
      en: "One side plays science; the other plays chance.",
      de: "Eine Seite spielt Wissenschaft; die andere spielt Zufall.",
      ru: "One side plays science; the other plays chance.",
    },
    cardDetail: {
      es: "Roles fijos durante toda la partida.",
      en: "Fixed roles for the whole match.",
      de: "Feste Rollen fuer die ganze Partie.",
      ru: "Fixed roles for the whole match.",
    },
    intro: {
      es: "Elige un torneo Ciencia V/S Azar y filtra por jugadores o tiempo.",
      en: "Choose a Science V/S Chance tournament and filter by players or time.",
      de: "Waehle ein Wissenschaft V/S Zufall-Turnier und filtere nach Spielern oder Zeit.",
      ru: "Choose a Science V/S Chance tournament and filter by players or time.",
    },
    onlyLabel: {
      es: "Solo torneos oficiales Ciencia V/S Azar.",
      en: "Only official Science V/S Chance tournaments.",
      de: "Nur offizielle Wissenschaft V/S Zufall-Turniere.",
      ru: "Only official Science V/S Chance tournaments.",
    },
    detail: {
      es: "Ciencia sin dados contra azar obligado a lanzar.",
      en: "Science without dice against chance forced to roll.",
      de: "Wissenschaft ohne Wuerfel gegen Zufall mit Wuerfelpflicht.",
      ru: "Science without dice against chance forced to roll.",
    },
    empty: {
      es: "No hay torneos de Ciencia V/S Azar abiertos para estos filtros.",
      en: "There are no open Science V/S Chance tournaments for these filters.",
      de: "Fuer diese Filter gibt es keine offenen Wissenschaft V/S Zufall-Turniere.",
      ru: "There are no open Science V/S Chance tournaments for these filters.",
    },
    panelDescription: {
      es: "Torneos oficiales del Modo Ciencia V/S Azar.",
      en: "Official Science V/S Chance Mode tournaments.",
      de: "Offizielle Turniere im Wissenschaft V/S Zufall-Modus.",
      ru: "Official Science V/S Chance Mode tournaments.",
    },
    guideMode: {
      es: "Solo aparecen torneos oficiales de Ciencia V/S Azar.",
      en: "Only official Science V/S Chance tournaments appear.",
      de: "Es erscheinen nur offizielle Wissenschaft V/S Zufall-Turniere.",
      ru: "Only official Science V/S Chance tournaments appear.",
    },
  },
};

const TOURNAMENT_COPY_OVERRIDES = Object.freeze({
  backToLobby: {
    es: "Volver al lobby",
    en: "Back to lobby",
    de: "Zurück zur Lobby",
    ru: "Назад в лобби",
  },
  all: {
    es: "Todos",
    en: "All",
    de: "Alle",
    ru: "Все",
  },
  openTournaments: {
    es: "Torneos abiertos",
    en: "Open tournaments",
    de: "Offene Turniere",
    ru: "Открытые турниры",
  },
  editTournament: {
    es: "Editar torneo",
    en: "Edit tournament",
    de: "Turnier bearbeiten",
    ru: "Редактировать турнир",
  },
  editTournamentOn: {
    es: "Editar torneo",
    en: "Edit tournament",
    de: "Turnier bearbeiten",
    ru: "Редактировать турнир",
  },
  mode: {
    es: "Modo",
    en: "Mode",
    de: "Modus",
    ru: "Режим",
  },
  filterPlayers: {
    es: "Filtra jugadores",
    en: "Filter players",
    de: "Spieler filtern",
    ru: "Фильтр игроков",
  },
  filterPlayersDescription: {
    es: "Elige la cantidad total de jugadores del torneo.",
    en: "Choose the total number of players in the tournament.",
    de: "Wähle die Gesamtzahl der Spieler im Turnier.",
    ru: "Выбери общее количество игроков в турнире.",
  },
  filterPlayersShortDescription: {
    es: "Elige la cantidad total de jugadores del torneo.",
    en: "Choose the total number of players in the tournament.",
    de: "Wähle die Gesamtzahl der Spieler im Turnier.",
    ru: "Выбери общее количество игроков в турнире.",
  },
  filterTime: {
    es: "Filtra tiempo",
    en: "Filter time",
    de: "Zeit filtern",
    ru: "Фильтр времени",
  },
  filterTimeDescription: {
    es: "Filtra por minutos de juego por turno.",
    en: "Filter by minutes of game time per turn.",
    de: "Filtere nach Spielminuten pro Zug.",
    ru: "Фильтруй по количеству минут на ход.",
  },
  filterTimeShortDescription: {
    es: "Filtra por minutos de juego por turno.",
    en: "Filter by minutes of game time per turn.",
    de: "Filtere nach Spielminuten pro Zug.",
    ru: "Фильтруй по количеству минут на ход.",
  },
  activeTournamentDescription: {
    es: "Torneo activo disponible para unirse.",
    en: "Active tournament available to join.",
    de: "Aktives Turnier verfügbar zum Beitreten.",
    ru: "Активный турнир доступен для входа.",
  },
  scheduledTournamentDescription: {
    es: "Torneo programado para iniciar pronto.",
    en: "Tournament scheduled to start soon.",
    de: "Turnier ist geplant und startet bald.",
    ru: "Турнир запланирован и скоро начнётся.",
  },
  active: {
    es: "Activo",
    en: "Active",
    de: "Aktiv",
    ru: "Активен",
  },
  startingSoon: {
    es: "Por empezar",
    en: "Starting soon",
    de: "Startet bald",
    ru: "Скоро начнётся",
  },
  starts: {
    es: "Empieza:",
    en: "Starts:",
    de: "Beginnt:",
    ru: "Начинается:",
  },
  ends: {
    es: "Termina:",
    en: "Ends:",
    de: "Endet:",
    ru: "Заканчивается:",
  },
  joined: {
    es: "Unidos:",
    en: "Joined:",
    de: "Beigetreten:",
    ru: "Присоединились:",
  },
  withPassword: {
    es: "Con contraseña",
    en: "With password",
    de: "Mit Passwort",
    ru: "С паролем",
  },
  payment: {
    es: "Pago {value}X",
    en: "Payment {value}X",
    de: "Zahlung {value}X",
    ru: "Оплата {value}X",
  },
  prize: {
    es: "Premio",
    en: "Prize",
    de: "Preis",
    ru: "Приз",
  },
  join: {
    es: "Unirse",
    en: "Join",
    de: "Beitreten",
    ru: "Присоединиться",
  },
  enter: {
    es: "Entrar",
    en: "Enter",
    de: "Eintreten",
    ru: "Войти",
  },
  opening: {
    es: "Abriendo...",
    en: "Opening...",
    de: "Wird geöffnet...",
    ru: "Открытие...",
  },
  open: {
    es: "Abrir",
    en: "Open",
    de: "Öffnen",
    ru: "Открыть",
  },
  edit: {
    es: "Editar",
    en: "Edit",
    de: "Bearbeiten",
    ru: "Редактировать",
  },
  registered: {
    es: "Inscrito",
    en: "Registered",
    de: "Angemeldet",
    ru: "Зарегистрирован",
  },
  loadingTournaments: {
    es: "Cargando torneos...",
    en: "Loading tournaments...",
    de: "Turniere werden geladen...",
    ru: "Загрузка турниров...",
  },
  noOpenTournaments: {
    es: "No hay torneos abiertos para este filtro.",
    en: "There are no open tournaments for this filter.",
    de: "Für diesen Filter gibt es keine offenen Turniere.",
    ru: "Для этого фильтра нет открытых турниров.",
  },
  noMoreOpenTournaments: {
    es: "No hay más torneos abiertos para este filtro.",
    en: "There are no more open tournaments for this filter.",
    de: "Für diesen Filter gibt es keine weiteren offenen Turniere.",
    ru: "Для этого фильтра больше нет открытых турниров.",
  },
  passwordTitle: {
    es: "Contraseña {value}",
    en: "Password {value}",
    de: "Passwort {value}",
    ru: "Пароль {value}",
  },
  passwordPrizePaymentTitle: {
    es: "Contraseña, premio y pago",
    en: "Password, prize and payment",
    de: "Passwort, Preis und Zahlung",
    ru: "Пароль, приз и оплата",
  },
  withAndWithoutChancePlayersTitle: {
    es: "Con y sin azar {players}",
    en: "With and without chance {players}",
    de: "Mit und ohne Zufall {players}",
    ru: "Со случайностью и без неё, {players}",
  },
  withAndWithoutDicePlayersTitle: {
    es: "Con y sin dados {players}",
    en: "With and without dice {players}",
    de: "Mit und ohne Würfel {players}",
    ru: "С кубиками и без них, {players}",
  },
  withDicePlayersTitle: {
    es: "Con dados {players}",
    en: "With dice {players}",
    de: "Mit Würfeln {players}",
    ru: "С кубиками {players}",
  },
  noDicePlayersTitle: {
    es: "Sin dados {players}",
    en: "No dice {players}",
    de: "Ohne Würfel {players}",
    ru: "Без кубиков {players}",
  },
  tournamentModeName: {
    es: "Torneo {mode}",
    en: "{mode} tournament",
    de: "{mode}-Turnier",
    ru: "Турнир {mode}",
  },
  howItWorks: {
    es: "Cómo funciona",
    en: "How it works",
    de: "So funktioniert es",
    ru: "Как это работает",
  },
  topPlayers: {
    es: "Mejores jugadores",
    en: "Top players",
    de: "Beste Spieler",
    ru: "Лучшие игроки",
  },
  viewRankingAria: {
    es: "Ver ranking",
    en: "View ranking",
    de: "Rangliste anzeigen",
    ru: "Посмотреть рейтинг",
  },
  noRankingAvailable: {
    es: "Sin ranking disponible.",
    en: "No ranking available.",
    de: "Keine Rangliste verfügbar.",
    ru: "Рейтинг недоступен.",
  },
  viewFullRanking: {
    es: "Ver ranking completo",
    en: "View full ranking",
    de: "Vollständige Rangliste anzeigen",
    ru: "Посмотреть полный рейтинг",
  },
  goToShop: {
    es: "Ir a la tienda",
    en: "Go to shop",
    de: "Zum Shop",
    ru: "В магазин",
  },
  shopDescription: {
    es: "Consigue la nueva edición y mejoras exclusivas",
    en: "Get the new edition and exclusive upgrades",
    de: "Hol dir die neue Edition und exklusive Verbesserungen",
    ru: "Получи новую версию и эксклюзивные улучшения",
  },
  championshipTitle: {
    es: "Campeonato Mundial XIMA 2026",
    en: "XIMA World Championship 2026",
    de: "XIMA-Weltmeisterschaft 2026",
    ru: "Чемпионат мира по XIMA 2026",
  },
  championshipDescription: {
    es: "Sé parte del torneo más grande y demuestra tu habilidad.",
    en: "Be part of the biggest tournament and prove your skill.",
    de: "Sei Teil des größten Turniers und beweise dein Können.",
    ru: "Стань частью самого большого турнира и докажи своё мастерство.",
  },
  saveChanges: {
    es: "Guardar cambios",
    en: "Save changes",
    de: "Änderungen speichern",
    ru: "Сохранить изменения",
  },
  cancel: {
    es: "Cancelar",
    en: "Cancel",
    de: "Abbrechen",
    ru: "Отмена",
  },
  selectedTournament: {
    es: "Torneo seleccionado",
    en: "Selected tournament",
    de: "Ausgewähltes Turnier",
    ru: "Выбранный турнир",
  },
  tournamentFallback: {
    es: "Torneo",
    en: "Tournament",
    de: "Turnier",
    ru: "Турнир",
  },
  missingId: {
    es: "sin id",
    en: "no id",
    de: "keine ID",
    ru: "без ID",
  },
  tournamentPassword: {
    es: "Contraseña del torneo",
    en: "Tournament password",
    de: "Turnierpasswort",
    ru: "Пароль турнира",
  },
  private: {
    es: "Privado",
    en: "Private",
    de: "Privat",
    ru: "Приватный",
  },
  public: {
    es: "Público",
    en: "Public",
    de: "Öffentlich",
    ru: "Публичный",
  },
  optionalNewPassword: {
    es: "Nueva contraseña opcional",
    en: "Optional new password",
    de: "Neues Passwort optional",
    ru: "Новый пароль необязателен",
  },
  requiredNewPassword: {
    es: "Nueva contraseña requerida",
    en: "New password required",
    de: "Neues Passwort erforderlich",
    ru: "Требуется новый пароль",
  },
  repairPrivateTournamentPassword: {
    es: "Este torneo privado está incompleto. Define una nueva contraseña para repararlo.",
    en: "This private tournament is incomplete. Set a new password to repair it.",
    de: "Dieses private Turnier ist unvollständig. Lege ein neues Passwort fest, um es zu reparieren.",
    ru: "Этот приватный турнир не завершён. Задай новый пароль, чтобы исправить его.",
  },
  tournamentName: {
    es: "Nombre del torneo",
    en: "Tournament name",
    de: "Turniername",
    ru: "Название турнира",
  },
  specialTournamentPlaceholder: {
    es: "Torneo especial",
    en: "Special tournament",
    de: "Spezialturnier",
    ru: "Особый турнир",
  },
  startDate: {
    es: "Fecha de inicio",
    en: "Start date",
    de: "Startdatum",
    ru: "Дата начала",
  },
  startTime: {
    es: "Hora de inicio",
    en: "Start time",
    de: "Startzeit",
    ru: "Время начала",
  },
  durationDays: {
    es: "Duración (días)",
    en: "Duration (days)",
    de: "Dauer (Tage)",
    ru: "Длительность (дни)",
  },
  rewardX: {
    es: "Recompensa (X)",
    en: "Reward (X)",
    de: "Belohnung (X)",
    ru: "Награда (X)",
  },
  winnerPrizeX: {
    es: "Premio ganador (X)",
    en: "Winner prize (X)",
    de: "Gewinnerpreis (X)",
    ru: "Приз победителя (X)",
  },
  timePerTurn: {
    es: "Tiempo por turno",
    en: "Time per turn",
    de: "Zeit pro Zug",
    ru: "Время на ход",
  },
  playerQuantity: {
    es: "Cantidad de jugadores",
    en: "Number of players",
    de: "Anzahl der Spieler",
    ru: "Количество игроков",
  },
});

const MODE_INTERFACE_COPY_OVERRIDES_BY_ID = Object.freeze({
  dice: {
    shortLabel: {
      es: "Azar",
      en: "Chance",
      de: "Zufall",
      ru: "Случайность",
    },
    routeLabel: {
      es: "Modo Azar",
      en: "Chance Mode",
      de: "Zufallsmodus",
      ru: "Режим случайности",
    },
    intro: {
      es: "Elige un torneo Azar y filtra por jugadores o tiempo de juego.",
      en: "Choose a Chance tournament and filter by players or game time.",
      de: "Wähle ein Zufallsturnier und filtere nach Spielern oder Spielzeit.",
      ru: "Выбери турнир в режиме случайности и фильтруй по игрокам или времени игры.",
    },
    onlyLabel: {
      es: "Solo torneos oficiales de Azar.",
      en: "Only official Chance tournaments.",
      de: "Nur offizielle Zufallsturniere.",
      ru: "Только официальные турниры режима случайности.",
    },
    detail: {
      es: "Torneos oficiales del Modo Azar.",
      en: "Official Chance Mode tournaments.",
      de: "Offizielle Turniere im Zufallsmodus.",
      ru: "Официальные турниры режима случайности.",
    },
    empty: {
      es: "No hay torneos de Modo Azar abiertos para estos filtros.",
      en: "There are no open Chance Mode tournaments for these filters.",
      de: "Für diese Filter gibt es keine offenen Turniere im Zufallsmodus.",
      ru: "Для этих фильтров нет открытых турниров режима случайности.",
    },
    panelDescription: {
      es: "Torneos oficiales del Modo Azar.",
      en: "Official Chance Mode tournaments.",
      de: "Offizielle Turniere im Zufallsmodus.",
      ru: "Официальные турниры режима случайности.",
    },
    guideMode: {
      es: "Solo aparecen torneos oficiales de Azar.",
      en: "Only official Chance tournaments appear.",
      de: "Es erscheinen nur offizielle Zufallsturniere.",
      ru: "Показываются только официальные турниры режима случайности.",
    },
  },
});

const getLocalizedTournamentEntry = (language, entry, fallback = "") => {
  const normalizedLanguage = normalizeLanguage(language);
  if (!entry || typeof entry !== "object") return fallback;
  return entry[normalizedLanguage] || entry.es || entry.en || fallback;
};

const getTournamentCopy = (language, key, values = {}) => {
  const source = TOURNAMENT_COPY_OVERRIDES[key] || TOURNAMENT_COPY[key];
  let copy = getLocalizedTournamentEntry(language, source, "");

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};

const getModeInterfaceCopy = (language, mode) => {
  const baseSource = MODE_INTERFACE_COPY_BY_ID[mode] || MODE_INTERFACE_COPY_BY_ID.free;
  const source = {
    ...baseSource,
    ...(MODE_INTERFACE_COPY_OVERRIDES_BY_ID[mode] || {}),
  };
  return Object.fromEntries(
    Object.entries(source).map(([key, value]) => [
      key,
      getLocalizedTournamentEntry(language, value, ""),
    ])
  );
};

const getTournamentModeLabel = (language, mode, key = "routeLabel") => {
  const modeCopy = getModeInterfaceCopy(language, mode);
  return modeCopy[key] || modeCopy.routeLabel || MODE_LABELS[mode] || "XIMA";
};

const formatTournamentNumber = (language, value, options = {}) =>
  new Intl.NumberFormat(getLocaleForLanguage(language), options).format(Number(value) || 0);

const formatTournamentSequenceLabel = (language, value) => {
  const formattedValue = formatTournamentNumber(language, value);
  const normalizedLanguage = normalizeLanguage(language);

  if (normalizedLanguage === "en") return `Tournament #${formattedValue}`;
  if (normalizedLanguage === "de") return `Turnier #${formattedValue}`;
  return `Torneo #${formattedValue}`;
};

const formatTournamentSequenceShortLabel = (language, value) =>
  `#${formatTournamentNumber(language, value)}`;

const normalizePublicSequenceNumberValue = (value) => {
  const parsed = Math.floor(Number(value) || 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const resolvePublicSequenceNumber = (entry) => {
  return (
    normalizePublicSequenceNumberValue(entry?.tournamentUiSequenceNumber) ||
    normalizePublicSequenceNumberValue(entry?.tournamentSequenceNumber) ||
    normalizePublicSequenceNumberValue(entry?.tournamentNumber) ||
    normalizePublicSequenceNumberValue(entry?.displayNumber) ||
    0
  );
};

const formatTournamentPlayerCountLabel = (language, count) => {
  const normalizedLanguage = normalizeLanguage(language);
  const value = Math.max(0, Math.round(Number(count) || 0));
  const formattedValue = formatTournamentNumber(normalizedLanguage, value);

  if (normalizedLanguage === "en") {
    return `${formattedValue} ${value === 1 ? "player" : "players"}`;
  }
  if (normalizedLanguage === "de") {
    return `${formattedValue} Spieler`;
  }
  if (normalizedLanguage === "ru") {
    const mod10 = value % 10;
    const mod100 = value % 100;
    const noun = mod10 === 1 && mod100 !== 11 ? "игрок" : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? "игрока" : "игроков";
    return `${formattedValue} ${noun}`;
  }
  return `${formattedValue} ${value === 1 ? "jugador" : "jugadores"}`;
};

const formatTournamentVersusLabel = (language, memberSize) => {
  const opponentCount = Math.max(1, Math.round(Number(memberSize) || 2) - 1);
  const formattedOpponentCount = formatTournamentNumber(language, opponentCount);
  const normalizedLanguage = normalizeLanguage(language);

  if (normalizedLanguage === "de") return `1 gegen ${formattedOpponentCount}`;
  if (normalizedLanguage === "ru") return `1 на ${formattedOpponentCount}`;
  return `1 vs ${formattedOpponentCount}`;
};

const formatTournamentTurnTimeLabel = (language, minutes) => {
  const value = Math.max(1, Math.round(Number(minutes) || DEFAULT_TURN_TIME));
  const formattedValue = formatTournamentNumber(language, value);
  const normalizedLanguage = normalizeLanguage(language);

  if (normalizedLanguage === "en") return `${formattedValue} min per turn`;
  if (normalizedLanguage === "de") return `${formattedValue} Min. pro Zug`;
  if (normalizedLanguage === "ru") return `${formattedValue} мин на ход`;
  return `${formattedValue} min por turno`;
};

const formatTournamentMinutesFilterLabel = (language, minutes) => {
  const value = Math.max(1, Math.round(Number(minutes) || DEFAULT_TURN_TIME));
  const formattedValue = formatTournamentNumber(language, value);
  const normalizedLanguage = normalizeLanguage(language);

  if (normalizedLanguage === "de") return `${formattedValue} Min.`;
  if (normalizedLanguage === "ru") return `${formattedValue} мин`;
  return `${formattedValue} min`;
};

const formatTournamentPrizeValue = (language, prize) =>
  typeof prize === "number" && Number.isFinite(prize) && prize > 0
    ? formatTournamentNumber(language, prize)
    : "XIMA";

const localizeTournamentNameForDisplay = (language, entry, mode, fallbackName = "") => {
  const rawName = String(fallbackName || resolveTournamentName(entry, mode)).trim();
  const normalizedName = rawName
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const passwordMatch = normalizedName.match(/^contrasena\s+(\d+)$/);
  const withAndWithoutChanceMatch = normalizedName.match(
    /^con y sin azar\s+(\d+)\s+jugadores$/
  );
  const withAndWithoutDiceMatch = normalizedName.match(
    /^con y sin dados\s+(\d+)\s+jugadores$/
  );
  const withDiceMatch = normalizedName.match(/^con dados\s+(\d+)\s+jugadores$/);
  const noDiceMatch = normalizedName.match(/^sin dados\s+(\d+)\s+jugadores$/);

  if (/^contrasena,\s*premio\s+y\s+pago$/.test(normalizedName)) {
    return getTournamentCopy(language, "passwordPrizePaymentTitle");
  }

  if (passwordMatch) {
    return getTournamentCopy(language, "passwordTitle", {
      value: formatTournamentNumber(language, Number(passwordMatch[1]) || 0),
    });
  }

  if (withAndWithoutChanceMatch) {
    const playerCount = Number(withAndWithoutChanceMatch[1]) || resolveMemberSize(entry);
    return getTournamentCopy(language, "withAndWithoutChancePlayersTitle", {
      players: formatTournamentPlayerCountLabel(language, playerCount),
    });
  }

  if (withAndWithoutDiceMatch) {
    const playerCount = Number(withAndWithoutDiceMatch[1]) || resolveMemberSize(entry);
    return getTournamentCopy(language, "withAndWithoutDicePlayersTitle", {
      players: formatTournamentPlayerCountLabel(language, playerCount),
    });
  }

  if (withDiceMatch) {
    const playerCount = Number(withDiceMatch[1]) || resolveMemberSize(entry);
    return getTournamentCopy(language, "withDicePlayersTitle", {
      players: formatTournamentPlayerCountLabel(language, playerCount),
    });
  }

  if (noDiceMatch) {
    const playerCount = Number(noDiceMatch[1]) || resolveMemberSize(entry);
    return getTournamentCopy(language, "noDicePlayersTitle", {
      players: formatTournamentPlayerCountLabel(language, playerCount),
    });
  }

  if (/^torneo\s+modo\s+(ciencia|azar|cienzar|ciencia\s+v\/s\s+azar)$/.test(normalizedName)) {
    return getTournamentCopy(language, "tournamentModeName", {
      mode: getTournamentModeLabel(language, mode, "routeLabel"),
    });
  }

  return rawName;
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);
const DAY_MS = 24 * 60 * 60 * 1000;

const parseLooseBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value !== "string") return false;
  const normalized = value.trim().toLowerCase();
  return ["true", "1", "yes", "on"].includes(normalized);
};

const parseTournamentRewardValue = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const sanitized = String(value || "").trim().replace(/[^0-9.-]/g, "");
  const fallback = Number(sanitized);
  return Number.isFinite(fallback) ? fallback : Number.NaN;
};

const normalizeTournamentRewardValue = (
  value,
  fallback = TOURNAMENT_DEFAULT_REWARD_VALUE
) => {
  const parsed = parseTournamentRewardValue(value);
  if (Number.isFinite(parsed)) {
    return Math.max(0, Math.min(MAX_TOURNAMENT_REWARD_VALUE, Math.floor(parsed)));
  }
  const fallbackParsed = parseTournamentRewardValue(fallback);
  if (Number.isFinite(fallbackParsed)) {
    return Math.max(0, Math.min(MAX_TOURNAMENT_REWARD_VALUE, Math.floor(fallbackParsed)));
  }
  return TOURNAMENT_DEFAULT_REWARD_VALUE;
};

const normalizeModeAlias = (value, fallback = "dice") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return fallback;
  if (normalized === "all") return "all";
  if (
    normalized.includes("sciencevsazar") ||
    normalized.includes("science-vs-azar") ||
    normalized.includes("science vs azar") ||
    normalized.includes("science v/s azar") ||
    normalized.includes("cienciavsazar") ||
    normalized.includes("ciencia-vs-azar") ||
    normalized.includes("ciencia vs azar") ||
    normalized.includes("ciencia v/s azar")
  ) {
    return "sciencevsazar";
  }
  if (normalized.includes("cienzar")) return "cienzar";
  if (normalized.includes("azar") || normalized.includes("dice")) return "dice";
  if (normalized.includes("ciencia") || normalized.includes("science") || normalized.includes("free")) {
    return "free";
  }
  return fallback;
};

const getTournamentId = (entry) =>
  String(entry?._id || entry?.id || entry?.tournamentId || "").trim();
const buildEntrySequenceLookup = (entries) =>
  ensureArray(entries)
    .filter((entry) => getTournamentId(entry))
    .sort(
      (a, b) =>
        new Date(b?.createdAt || b?.startingTime || 0).valueOf() -
        new Date(a?.createdAt || a?.startingTime || 0).valueOf()
    )
    .reduce((acc, entry, index) => {
      acc[getTournamentId(entry)] = index + 1;
      return acc;
    }, {});

const normalizeIdentity = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getLastJoinedTournamentStorageKey = (email) => {
  const normalizedEmail = normalizeIdentity(email || "guest");
  return `${LAST_JOINED_TOURNAMENT_PREFIX}${normalizedEmail || "guest"}`;
};

const parseStoredLastJoinedTournamentId = (rawValue) => {
  const raw = String(rawValue || "").trim();
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw);
    return String(parsed?.tournamentId || parsed?.id || raw).trim();
  } catch {
    return raw;
  }
};

const getStoredLastJoinedTournamentId = (storageKey) => {
  if (typeof window === "undefined") return "";
  const key = String(storageKey || "").trim();
  if (!key) return "";
  return parseStoredLastJoinedTournamentId(window.localStorage.getItem(key));
};

const getUserIdentitySet = (user) => {
  const identities = new Set();
  const addIdentity = (value) => {
    const normalized = normalizeIdentity(value);
    if (!normalized) return;
    identities.add(normalized);
    if (normalized.includes("@")) {
      const localPart = normalized.split("@")[0];
      if (localPart) identities.add(localPart);
    }
  };

  [
    user?.email,
    user?.loginIdentifier,
    user?.username,
    user?.userName,
    user?.firstName,
    user?.displayName,
    user?.playerCode,
    user?.userId,
    user?._id,
    user?.id,
  ].forEach(addIdentity);

  return identities;
};

const getParticipantIdentityCandidates = (entry) => {
  if (!entry) return [];
  if (typeof entry !== "object") return [entry];
  return [
    entry.userIdNormalized,
    entry.userId,
    entry._id,
    entry.id,
    entry.email,
    entry.loginIdentifier,
    entry.identity,
    entry.username,
    entry.userName,
    entry.name,
    entry.displayName,
    entry.nickName,
    entry.nickname,
    entry.playerCode,
  ];
};

const isCurrentUserRegisteredForTournament = (entry, userIdentitySet) => {
  if (!entry || !(userIdentitySet instanceof Set) || userIdentitySet.size === 0) return false;

  return [
    ...ensureArray(entry?.users),
    ...ensureArray(entry?.registeredPlayers),
    ...ensureArray(entry?.paidUsers),
    ...ensureArray(entry?.players),
  ].some((participant) =>
    getParticipantIdentityCandidates(participant)
      .map(normalizeIdentity)
      .some((identity) => identity && userIdentitySet.has(identity))
  );
};

const resolveTournamentMode = (entry) => {
  const normalizedMode = normalizeModeAlias(entry?.mode, "");
  if (
    normalizedMode === "free" ||
    normalizedMode === "dice" ||
    normalizedMode === "cienzar" ||
    normalizedMode === "sciencevsazar"
  ) {
    return normalizedMode;
  }
  const baseMode = normalizeModeAlias(entry?.freeBoxBaseMode, "");
  return baseMode === "free" ||
    baseMode === "dice" ||
    baseMode === "cienzar" ||
    baseMode === "sciencevsazar"
    ? baseMode
    : "free";
};

const resolveTournamentName = (entry, mode) => {
  const name = String(entry?.tournamentName || entry?.name || entry?.title || "").trim();
  if (name) return name;
  return `Torneo ${MODE_LABELS[mode] || "XIMA"}`;
};

const resolvePrize = (entry) => {
  const candidates = [
    entry?.tournamentWinnerPrizeValue,
    entry?.winnerPrizeValue,
    entry?.winnerPrize,
    entry?.tournamentRewardValue,
    entry?.rewardValue,
    entry?.rewardAmount,
    entry?.reward,
    entry?.prize,
  ];
  const value = candidates.find((item) => Number(item) > 0);
  return Math.max(0, Math.round(Number(value) || 0));
};

const resolveTournamentWinnerPrizeValue = (entry) => {
  const candidate = [
    entry?.tournamentWinnerPrizeValue,
    entry?.winnerPrizeValue,
    entry?.winnerPrize,
  ].find((value) => Number.isFinite(parseTournamentRewardValue(value)));
  return normalizeTournamentRewardValue(candidate, TOURNAMENT_DEFAULT_REWARD_VALUE);
};

const resolvePlayerCount = (entry) => {
  const joined = ensureArray(entry?.users).length;
  const registered = ensureArray(entry?.registeredPlayers).length;
  return Math.max(
    0,
    Number(entry?.connectedPlayers) || 0,
    Number(entry?.joinedPlayersCount) || 0,
    Number(entry?.registeredPlayersCount) || 0,
    Number(entry?.playersCount) || 0,
    Number(entry?.usersCount) || 0,
    joined,
    registered
  );
};
const resolveVisibleJoinedCount = (value) =>
  Math.max(0, Math.floor(Number(value) || 0) - 1);

const resolveMemberSize = (entry) => Math.max(2, Number(entry?.memberSize || entry?.members) || 2);
const resolveTurnTime = (entry) => Math.max(1, Number(entry?.havingTime || entry?.turnTime) || 5);

const resolveEntryFee = (entry) => {
  const value = [
    entry?.tournamentRewardValue,
    entry?.entryFee,
    entry?.fee,
    entry?.paymentAmount,
    entry?.requiredPayment,
  ].find((item) => Number(item) > 0);
  return Math.max(0, Math.round(Number(value) || 0));
};

const formatDateInputValue = (value) => {
  const date = new Date(value || Date.now() + 60 * 1000);
  if (Number.isNaN(date.valueOf())) return formatDateInputValue(Date.now() + 60 * 1000);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const formatTimeInputValue = (value) => {
  const date = new Date(value || Date.now() + 60 * 1000);
  if (Number.isNaN(date.valueOf())) return formatTimeInputValue(Date.now() + 60 * 1000);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(
    2,
    "0"
  )}`;
};

const parseLocalDateTimeInput = (dateValue, timeValue) => {
  const dateText = String(dateValue || "").trim();
  const timeText = String(timeValue || "").trim();
  if (!dateText || !timeText) return null;
  const parsed = new Date(`${dateText}T${timeText}:00`);
  return Number.isNaN(parsed.valueOf()) ? null : parsed;
};

const resolveTimestampMs = (value) => {
  const timestamp = new Date(value || 0).valueOf();
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0;
};

const resolveTournamentTiming = (entry, nowMs = Date.now()) => {
  const startMs = resolveTimestampMs(entry?.startingTime || entry?.start || entry?.createdAt);
  const durationDays = Math.max(0, Number(entry?.duration) || 0);
  const endMs = startMs > 0 && durationDays > 0 ? startMs + durationDays * DAY_MS : 0;
  return {
    startMs,
    endMs,
    startRemainingMs: startMs > nowMs ? startMs - nowMs : 0,
    endRemainingMs: endMs > nowMs ? endMs - nowMs : 0,
  };
};

const formatCountdownLabel = (remainingMs) => {
  const safeMs = Math.max(0, Number(remainingMs) || 0);
  const totalSeconds = Math.floor(safeMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const time = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
    seconds
  ).padStart(2, "0")}`;
  return days > 0 ? `${days}d ${time}` : time;
};

const getTournamentStatus = (entry, nowMs = Date.now()) => {
  const explicit = String(entry?.status || entry?.state || "")
    .trim()
    .toLowerCase();
  if (["finished", "expired", "ended", "closed"].includes(explicit)) return "finished";

  const { startMs, endMs } = resolveTournamentTiming(entry, nowMs);
  if (!startMs) return "playing";
  if (endMs > 0 && nowMs > endMs) return "finished";
  if (startMs > nowMs) return "waiting";
  return "playing";
};

const isAutoGeneratedChallengeName = (value) =>
  /^desaf[i\u00ed]o creado por\b/i.test(String(value || "").trim());

const isOfficialAdminTournament = (entry) => {
  if (!entry || typeof entry !== "object") return false;
  if (Object.prototype.hasOwnProperty.call(entry, "isSuperuserCreated")) {
    return parseLooseBoolean(entry.isSuperuserCreated);
  }

  const explicitAdminFlags = [
    entry?.isAdminCreated,
    entry?.adminCreated,
    entry?.createdByAdmin,
    entry?.createdFromAdmin,
    entry?.createdInAdmin,
  ];
  if (explicitAdminFlags.some(parseLooseBoolean)) return true;

  const roleCandidates = [
    entry?.createdByRole,
    entry?.creatorRole,
    entry?.createdByUserRole,
    entry?.ownerRole,
  ].map((value) =>
    String(value || "")
      .trim()
      .toLowerCase()
  );

  return roleCandidates.some((value) => value === "admin" || value === "superuser");
};

const shouldShowTournament = (entry) => {
  const id = getTournamentId(entry);
  if (!id) return false;
  if (!isOfficialAdminTournament(entry)) return false;
  if (parseLooseBoolean(entry?.isIndependentMatch)) return false;
  if (parseLooseBoolean(entry?.isPuzzle) || parseLooseBoolean(entry?.isPuzzleMatch)) return false;
  if (isAutoGeneratedChallengeName(entry?.tournamentName || entry?.name || entry?.title)) return false;
  const mode = resolveTournamentMode(entry);
  if (!["free", "dice", "cienzar", "sciencevsazar"].includes(mode)) return false;
  return getTournamentStatus(entry) !== "finished";
};

const extractTournamentEntries = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const directKeys = ["tournaments", "matches", "lobbies", "rooms", "data", "items"];
  const directEntries = directKeys.flatMap((key) => extractTournamentEntries(payload[key]));
  if (directEntries.length > 0) return directEntries;

  return Object.values(payload).flatMap((value) => (Array.isArray(value) ? value : []));
};

const mergeTournamentLists = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((entry) => {
    const id = getTournamentId(entry);
    if (!id) return;
    byId.set(id, { ...(byId.get(id) || {}), ...entry });
  });
  return Array.from(byId.values());
};

const sortTournaments = (items) => {
  const statusWeight = { playing: 0, waiting: 1, unknown: 2 };
  return [...items].sort((a, b) => {
    const statusA = statusWeight[getTournamentStatus(a)] ?? 3;
    const statusB = statusWeight[getTournamentStatus(b)] ?? 3;
    if (statusA !== statusB) return statusA - statusB;
    return (
      new Date(b?.createdAt || b?.startingTime || 0).valueOf() -
      new Date(a?.createdAt || a?.startingTime || 0).valueOf()
    );
  });
};

const buildLeaderboardRows = (leaderboardPayload) => {
  const modes = leaderboardPayload?.modes || {};
  const rows = Object.values(modes)
    .flatMap((entries) => ensureArray(entries))
    .map((entry, index) => ({
      rank: Math.max(1, Number(entry?.rank) || index + 1),
      name:
        String(entry?.displayName || entry?.username || entry?.userName || entry?.email || "").trim() ||
        "Jugador",
      playerCode: String(entry?.playerCode || "").trim().toUpperCase(),
      score: Math.max(0, Math.round(Number(entry?.cimaxScore || entry?.wins) || 0)),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  return rows.map((row, index) => ({ ...row, rank: index + 1 }));
};

const getInitialModeFilter = (location) => {
  const params = new URLSearchParams(location.search || "");
  const queryMode = normalizeModeAlias(params.get("mode"), "all");
  const stateMode = normalizeModeAlias(location.state?.mode || location.state?.tournamentMode, "all");
  return ["free", "dice", "cienzar", "sciencevsazar"].includes(queryMode)
    ? queryMode
    : ["free", "dice", "cienzar", "sciencevsazar"].includes(stateMode)
    ? stateMode
    : "all";
};

const normalizeDedicatedMode = (value, fallback = "") => {
  const normalizedMode = normalizeModeAlias(value, fallback);
  return ["free", "dice", "cienzar", "sciencevsazar"].includes(normalizedMode)
    ? normalizedMode
    : fallback;
};

const resolveModeInterfaceRoute = (location) => {
  const params = new URLSearchParams(location.search || "");
  const explicitMode = normalizeDedicatedMode(
    params.get("modeInterface") ||
      params.get("dedicatedMode") ||
      location.state?.modeInterface ||
      location.state?.dedicatedMode,
    ""
  );
  if (explicitMode) return explicitMode;

  const queryView = String(params.get("view") || params.get("interface") || "")
    .trim()
    .toLowerCase();
  if (queryView === "science" || queryView === "modo-ciencia") return "free";
  if (queryView === "luck" || queryView === "azar" || queryView === "modo-azar") return "dice";
  if (queryView === "cienzar" || queryView === "modo-cienzar") return "cienzar";
  if (
    queryView === "sciencevsazar" ||
    queryView === "ciencia-vs-azar" ||
    queryView === "modo-ciencia-vs-azar"
  ) {
    return "sciencevsazar";
  }

  if (parseLooseBoolean(params.get("scienceMode")) || parseLooseBoolean(location.state?.scienceMode)) {
    return "free";
  }

  const source = String(location.state?.source || "").trim().toLowerCase();
  return (
    Object.entries(MODE_INTERFACE_LOBBY_SOURCE_BY_ID).find(
      ([, sourceName]) => sourceName === source
    )?.[0] || ""
  );
};

const getInitialNumericFilter = (location, key) => {
  const params = new URLSearchParams(location.search || "");
  const queryValue = String(params.get(key) || "").trim();
  const stateValue = String(location.state?.[key] || "").trim();
  const value = queryValue || stateValue;
  if (!value || value.toLowerCase() === "all") return "all";
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? String(Math.round(numericValue)) : "all";
};

const buildNumericFilterOptions = (items, resolver, fallbackValues, allLabel, labelBuilder) => {
  const values = new Set(fallbackValues);
  ensureArray(items).forEach((entry) => {
    const value = Math.round(Number(resolver(entry)) || 0);
    if (value > 0) values.add(value);
  });

  return [
    { value: "all", label: allLabel },
    ...Array.from(values)
      .sort((a, b) => a - b)
      .map((value) => ({ value: String(value), label: labelBuilder(value) })),
  ];
};

const PlayTournaments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth() || {};
  const outletContext = useOutletContext() || {};
  const language = useInterfaceLanguage(outletContext.language);
  const tournamentText = useCallback(
    (key, values) => getTournamentCopy(language, key, values),
    [language]
  );
  const modeInterfaceMode = useMemo(
    () => resolveModeInterfaceRoute(location),
    [location]
  );
  const isModeInterface = Boolean(modeInterfaceMode);
  const activeModeInterface = modeInterfaceMode || "free";
  const modeInterfaceOption =
    MODE_OPTIONS.find((option) => option.id === activeModeInterface) || MODE_OPTIONS[0];
  const modeInterfaceTheme = MODE_THEME_BY_ID[activeModeInterface] || MODE_THEME_BY_ID.free;
  const modeInterfaceCopy = useMemo(
    () => getModeInterfaceCopy(language, activeModeInterface),
    [activeModeInterface, language]
  );
  const [modeFilter, setModeFilter] = useState(() =>
    resolveModeInterfaceRoute(location) || getInitialModeFilter(location)
  );
  const [modePlayerFilter, setModePlayerFilter] = useState(() =>
    getInitialNumericFilter(location, "players")
  );
  const [modeTimeFilter, setModeTimeFilter] = useState(() =>
    getInitialNumericFilter(location, "time")
  );
  const [tournaments, setTournaments] = useState([]);
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openingTournamentId, setOpeningTournamentId] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [lastJoinedTournamentId, setLastJoinedTournamentId] = useState("");
  const [tournamentEditModeEnabled, setTournamentEditModeEnabled] = useState(false);
  const [tournamentEditModalOpen, setTournamentEditModalOpen] = useState(false);
  const [tournamentEditSaving, setTournamentEditSaving] = useState(false);
  const [tournamentEditTargetId, setTournamentEditTargetId] = useState("");
  const [tournamentEditTargetLabel, setTournamentEditTargetLabel] = useState("");
  const [tournamentEditNameDraft, setTournamentEditNameDraft] = useState("");
  const [tournamentEditStartDateDraft, setTournamentEditStartDateDraft] = useState("");
  const [tournamentEditStartTimeDraft, setTournamentEditStartTimeDraft] = useState("");
  const [tournamentEditDurationDraft, setTournamentEditDurationDraft] = useState(1);
  const [tournamentEditHavingTimeDraft, setTournamentEditHavingTimeDraft] =
    useState(DEFAULT_TURN_TIME);
  const [tournamentEditMemberSizeDraft, setTournamentEditMemberSizeDraft] = useState(
    MEMBER_OPTIONS[0]
  );
  const [tournamentEditRewardValueDraft, setTournamentEditRewardValueDraft] = useState(
    TOURNAMENT_DEFAULT_REWARD_VALUE
  );
  const [tournamentEditWinnerPrizeValueDraft, setTournamentEditWinnerPrizeValueDraft] =
    useState(TOURNAMENT_DEFAULT_REWARD_VALUE);
  const [tournamentEditInitialHasMatchPassword, setTournamentEditInitialHasMatchPassword] =
    useState(false);
  const [tournamentEditMatchPasswordEnabledDraft, setTournamentEditMatchPasswordEnabledDraft] =
    useState(false);
  const [tournamentEditMatchPasswordConfigured, setTournamentEditMatchPasswordConfigured] =
    useState(true);
  const [tournamentEditMatchPasswordDraft, setTournamentEditMatchPasswordDraft] = useState("");

  const userIdentitySet = useMemo(() => getUserIdentitySet(user), [user]);
  const hasAdminTournamentAccess = useMemo(
    () =>
      [
        user?.role,
        user?.type,
        user?.permission,
        user?.accountType,
        user?.accessLevel,
      ]
        .map(normalizeIdentity)
        .some((value) => value === "admin" || value === "superuser"),
    [user?.accessLevel, user?.accountType, user?.permission, user?.role, user?.type]
  );
  const lastJoinedTournamentStorageKey = useMemo(
    () => getLastJoinedTournamentStorageKey(user?.email),
    [user?.email]
  );

  const resetTournamentEditDrafts = useCallback(() => {
    setTournamentEditModalOpen(false);
    setTournamentEditSaving(false);
    setTournamentEditTargetId("");
    setTournamentEditTargetLabel("");
    setTournamentEditNameDraft("");
    setTournamentEditStartDateDraft("");
    setTournamentEditStartTimeDraft("");
    setTournamentEditDurationDraft(1);
    setTournamentEditHavingTimeDraft(DEFAULT_TURN_TIME);
    setTournamentEditMemberSizeDraft(MEMBER_OPTIONS[0]);
    setTournamentEditRewardValueDraft(TOURNAMENT_DEFAULT_REWARD_VALUE);
    setTournamentEditWinnerPrizeValueDraft(TOURNAMENT_DEFAULT_REWARD_VALUE);
    setTournamentEditInitialHasMatchPassword(false);
    setTournamentEditMatchPasswordEnabledDraft(false);
    setTournamentEditMatchPasswordConfigured(true);
    setTournamentEditMatchPasswordDraft("");
  }, []);

  useEffect(() => {
    if (hasAdminTournamentAccess) return;
    setTournamentEditModeEnabled(false);
    resetTournamentEditDrafts();
  }, [hasAdminTournamentAccess, resetTournamentEditDrafts]);

  useEffect(() => {
    const syncLastJoinedTournament = () => {
      setLastJoinedTournamentId(
        getStoredLastJoinedTournamentId(lastJoinedTournamentStorageKey)
      );
    };

    syncLastJoinedTournament();
    window.addEventListener("focus", syncLastJoinedTournament);
    return () => window.removeEventListener("focus", syncLastJoinedTournament);
  }, [lastJoinedTournamentStorageKey]);

  // Solo el dueño del sitio: botón para eliminar un torneo en modo editar.
  const isOwner = isOwnerAccount(user);
  const [ownerDeletingId, setOwnerDeletingId] = useState("");

  const loadTournaments = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [tournamentResult, liveResult, leaderboardResult] = await Promise.allSettled([
        axios.get("/tournaments"),
        axios.get("/tournaments/live"),
        axios.get("/tournaments/leaderboard/modes"),
      ]);

      const tournamentEntries =
        tournamentResult.status === "fulfilled"
          ? extractTournamentEntries(tournamentResult.value?.data)
          : [];
      const liveEntries =
        liveResult.status === "fulfilled" ? extractTournamentEntries(liveResult.value?.data) : [];
      const nextTournaments = sortTournaments(
        mergeTournamentLists(tournamentEntries, liveEntries).filter(shouldShowTournament)
      );

      setTournaments(nextTournaments);
      if (leaderboardResult.status === "fulfilled") {
        setLeaderboardRows(buildLeaderboardRows(leaderboardResult.value?.data));
      }
    } catch (error) {
      console.error(error);
      notification.error({
        message: "Torneos",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "No se pudieron cargar los torneos.",
      });
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const closeTournamentEditModal = useCallback(() => {
    if (tournamentEditSaving) return;
    resetTournamentEditDrafts();
  }, [resetTournamentEditDrafts, tournamentEditSaving]);

  const handleToggleTournamentEditMode = useCallback(() => {
    if (!hasAdminTournamentAccess) {
      notification.warning({
        message: i18nT("lobby.soloAdministradores"),
        description: i18nT("lobby.laEdicionDeTorneosSoloEstaDisponiblePara"),
      });
      return;
    }

    setTournamentEditModeEnabled((current) => {
      const next = !current;
      if (next) {
        notification.info({
          message: i18nT("lobby.modoEdicionActivado"),
          description:
            i18nT("tournaments.presionaLaImagenOElLogoDelTorneo"),
        });
      } else {
        resetTournamentEditDrafts();
      }
      return next;
    });
  }, [hasAdminTournamentAccess, resetTournamentEditDrafts]);

  const openTournamentEditModal = useCallback(
    (tournamentRecord) => {
      if (!hasAdminTournamentAccess || !tournamentEditModeEnabled) return;
      const tournamentId = getTournamentId(tournamentRecord);
      if (!tournamentId) return;

      const startValue =
        tournamentRecord?.startingTime ||
        tournamentRecord?.start ||
        Date.now() + 60 * 1000;
      const parsedHavingTime = Number(resolveTurnTime(tournamentRecord));
      const resolvedHavingTime = TOURNAMENT_EDIT_HAVING_TIME_OPTIONS.includes(parsedHavingTime)
        ? parsedHavingTime
        : DEFAULT_TURN_TIME;
      const parsedMemberSize = Number(resolveMemberSize(tournamentRecord));
      const resolvedMemberSize = MEMBER_OPTIONS.includes(parsedMemberSize)
        ? parsedMemberSize
        : MEMBER_OPTIONS[0];
      const fallbackName = resolveTournamentName(
        tournamentRecord,
        resolveTournamentMode(tournamentRecord)
      );

      setTournamentEditTargetId(tournamentId);
      setTournamentEditTargetLabel(fallbackName);
      setTournamentEditNameDraft(
        String(tournamentRecord?.tournamentName || tournamentRecord?.name || fallbackName)
          .trim()
          .slice(0, 120)
      );
      setTournamentEditStartDateDraft(formatDateInputValue(startValue));
      setTournamentEditStartTimeDraft(formatTimeInputValue(startValue));
      setTournamentEditDurationDraft(Math.max(1, Math.floor(Number(tournamentRecord?.duration) || 1)));
      setTournamentEditHavingTimeDraft(resolvedHavingTime);
      setTournamentEditMemberSizeDraft(resolvedMemberSize);
      setTournamentEditRewardValueDraft(
        normalizeTournamentRewardValue(resolveEntryFee(tournamentRecord), TOURNAMENT_DEFAULT_REWARD_VALUE)
      );
      setTournamentEditWinnerPrizeValueDraft(resolveTournamentWinnerPrizeValue(tournamentRecord));
      const hasMatchPassword = parseLooseBoolean(tournamentRecord?.hasMatchPassword);
      setTournamentEditInitialHasMatchPassword(hasMatchPassword);
      setTournamentEditMatchPasswordEnabledDraft(hasMatchPassword);
      setTournamentEditMatchPasswordConfigured(
        tournamentRecord?.matchPasswordConfigured !== false &&
          tournamentRecord?.matchPasswordInvalid !== true
      );
      setTournamentEditMatchPasswordDraft("");
      setTournamentEditModalOpen(true);
    },
    [hasAdminTournamentAccess, tournamentEditModeEnabled]
  );

  const handleSaveTournamentEdit = useCallback(async () => {
    if (!hasAdminTournamentAccess) {
      notification.warning({
        message: i18nT("lobby.soloAdministradores"),
        description: i18nT("lobby.laEdicionDeTorneosSoloEstaDisponiblePara"),
      });
      return;
    }

    const targetTournamentId = String(tournamentEditTargetId || "").trim();
    if (!targetTournamentId) {
      notification.warning({
        message: i18nT("lobby.torneoNoSeleccionado"),
        description: i18nT("lobby.seleccionaUnTorneoDesdeSuImagenAntesDe"),
      });
      return;
    }

    const resolvedDuration = Math.max(1, Math.floor(Number(tournamentEditDurationDraft) || 0));
    if (!Number.isFinite(resolvedDuration) || resolvedDuration < 1) {
      notification.warning({
        message: i18nT("lobby.duracionInvalida"),
        description: i18nT("lobby.laDuracionDelTorneoDebeSerMinimoDia"),
      });
      return;
    }

    const resolvedHavingTime = Number(tournamentEditHavingTimeDraft);
    if (!TOURNAMENT_EDIT_HAVING_TIME_OPTIONS.includes(resolvedHavingTime)) {
      notification.warning({
        message: i18nT("lobby.tiempoPorTurnoInvalido"),
        description: i18nT("tournaments.seleccionaUnValorValidoOMinutos"),
      });
      return;
    }

    const resolvedMemberSize = Number(tournamentEditMemberSizeDraft);
    if (!MEMBER_OPTIONS.includes(resolvedMemberSize)) {
      notification.warning({
        message: i18nT("lobby.cantidadDeJugadoresInvalida"),
        description: i18nT("lobby.seleccionaUnaCantidadValidaDeJugadores"),
      });
      return;
    }

    const resolvedStart = parseLocalDateTimeInput(
      tournamentEditStartDateDraft,
      tournamentEditStartTimeDraft
    );
    if (!resolvedStart) {
      notification.warning({
        message: i18nT("lobby.fechaUHoraInvalida"),
        description: i18nT("lobby.defineUnaFechaYHoraDeInicioValidas"),
      });
      return;
    }

    const resolvedName =
      String(tournamentEditNameDraft || "").trim().slice(0, 120) ||
      String(tournamentEditTargetLabel || "Torneo").trim().slice(0, 120);
    const resolvedRewardValue = normalizeTournamentRewardValue(
      tournamentEditRewardValueDraft,
      TOURNAMENT_DEFAULT_REWARD_VALUE
    );
    const resolvedWinnerPrizeValue = normalizeTournamentRewardValue(
      tournamentEditWinnerPrizeValueDraft,
      TOURNAMENT_DEFAULT_REWARD_VALUE
    );
    const normalizedMatchPassword = normalizeMatchPassword(tournamentEditMatchPasswordDraft);
    const passwordEnabledChanged =
      Boolean(tournamentEditMatchPasswordEnabledDraft) !==
      Boolean(tournamentEditInitialHasMatchPassword);
    const passwordValueChanged = Boolean(normalizedMatchPassword);
    const mustRepairPassword =
      Boolean(tournamentEditMatchPasswordEnabledDraft) &&
      !Boolean(tournamentEditMatchPasswordConfigured);
    const shouldSendPasswordConfig =
      passwordEnabledChanged || passwordValueChanged || mustRepairPassword;

    if (
      shouldSendPasswordConfig &&
      Boolean(tournamentEditMatchPasswordEnabledDraft) &&
      !isValidMatchPassword(normalizedMatchPassword)
    ) {
      notification.warning({
        message: i18nT("lobby.contrasenaRequerida"),
        description:
          i18nT("tournaments.defineUnaContrasenaDeADigitosParaActivar"),
      });
      return;
    }

    const passwordPayload =
      shouldSendPasswordConfig
        ? {
            hasMatchPassword: Boolean(tournamentEditMatchPasswordEnabledDraft),
            matchPassword: Boolean(tournamentEditMatchPasswordEnabledDraft)
              ? normalizedMatchPassword
              : "",
            tournamentPassword: Boolean(tournamentEditMatchPasswordEnabledDraft)
              ? normalizedMatchPassword
              : "",
            joinPassword: Boolean(tournamentEditMatchPasswordEnabledDraft)
              ? normalizedMatchPassword
              : "",
          }
        : {};

    try {
      setTournamentEditSaving(true);
      await axios.post("/tournaments/admin/tournaments/update", {
        tournamentId: targetTournamentId,
        tournamentName: resolvedName,
        startingTime: resolvedStart.toISOString(),
        duration: resolvedDuration,
        havingTime: resolvedHavingTime,
        memberSize: resolvedMemberSize,
        tournamentRewardValue: resolvedRewardValue,
        tournamentWinnerPrizeValue: resolvedWinnerPrizeValue,
        ...passwordPayload,
      });

      await loadTournaments(false);
      notification.success({
        message: i18nT("lobby.torneoActualizado"),
        description: `Cambios guardados en "${resolvedName}".`,
      });
      resetTournamentEditDrafts();
    } catch (error) {
      notification.error({
        message: i18nT("lobby.errorActualizandoTorneo"),
        description:
          error?.response?.data?.message || error?.message || "No se pudo actualizar el torneo.",
      });
    } finally {
      setTournamentEditSaving(false);
    }
  }, [
    hasAdminTournamentAccess,
    loadTournaments,
    resetTournamentEditDrafts,
    tournamentEditDurationDraft,
    tournamentEditHavingTimeDraft,
    tournamentEditMemberSizeDraft,
    tournamentEditNameDraft,
    tournamentEditRewardValueDraft,
    tournamentEditStartDateDraft,
    tournamentEditStartTimeDraft,
    tournamentEditTargetId,
    tournamentEditTargetLabel,
    tournamentEditInitialHasMatchPassword,
    tournamentEditMatchPasswordConfigured,
    tournamentEditMatchPasswordDraft,
    tournamentEditMatchPasswordEnabledDraft,
    tournamentEditWinnerPrizeValueDraft,
  ]);

  useEffect(() => {
    loadTournaments(false);
    const timer = window.setInterval(() => {
      loadTournaments(true);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [loadTournaments]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isModeInterface) {
      setModeFilter(activeModeInterface);
    }
  }, [activeModeInterface, isModeInterface]);

  const modeInterfaceTournaments = useMemo(
    () => tournaments.filter((entry) => resolveTournamentMode(entry) === activeModeInterface),
    [activeModeInterface, tournaments]
  );

  const modePlayerOptions = useMemo(
    () =>
      buildNumericFilterOptions(
        modeInterfaceTournaments,
        resolveMemberSize,
        DEFAULT_PLAYER_FILTER_VALUES,
        tournamentText("all"),
        (value) => formatTournamentPlayerCountLabel(language, value)
      ),
    [language, modeInterfaceTournaments, tournamentText]
  );

  const modeTimeOptions = useMemo(
    () =>
      buildNumericFilterOptions(
        modeInterfaceTournaments,
        resolveTurnTime,
        DEFAULT_TIME_FILTER_VALUES,
        tournamentText("all"),
        (value) => formatTournamentMinutesFilterLabel(language, value)
      ),
    [language, modeInterfaceTournaments, tournamentText]
  );

  const filterOptions = useMemo(
    () => [
      { value: "all", label: tournamentText("allModes") },
      { value: "free", label: getTournamentModeLabel(language, "free", "shortLabel") },
      { value: "dice", label: getTournamentModeLabel(language, "dice", "shortLabel") },
      { value: "cienzar", label: getTournamentModeLabel(language, "cienzar", "shortLabel") },
      {
        value: "sciencevsazar",
        label: getTournamentModeLabel(language, "sciencevsazar", "shortLabel"),
      },
    ],
    [language, tournamentText]
  );

  const filteredTournaments = useMemo(() => {
    const modeFiltered = isModeInterface
      ? modeInterfaceTournaments
      : modeFilter === "all"
      ? tournaments
      : tournaments.filter((entry) => resolveTournamentMode(entry) === modeFilter);

    if (!isModeInterface) return modeFiltered;

    return modeFiltered.filter((entry) => {
      const matchesPlayers =
        modePlayerFilter === "all" ||
        String(resolveMemberSize(entry)) === String(modePlayerFilter);
      const matchesTime =
        modeTimeFilter === "all" || String(resolveTurnTime(entry)) === String(modeTimeFilter);
      return matchesPlayers && matchesTime;
    });
  }, [
    isModeInterface,
    modeFilter,
    modeInterfaceTournaments,
    modePlayerFilter,
    modeTimeFilter,
    tournaments,
  ]);

  const visibleTournaments = filteredTournaments;
  const tournamentFallbackSequenceById = useMemo(
    () => buildEntrySequenceLookup(tournaments),
    [tournaments]
  );

  const registeredTournamentScope = useMemo(() => {
    if (isModeInterface) return modeInterfaceTournaments;
    if (modeFilter === "all") return tournaments;
    return tournaments.filter((entry) => resolveTournamentMode(entry) === modeFilter);
  }, [isModeInterface, modeFilter, modeInterfaceTournaments, tournaments]);

  const featuredRegisteredTournament = useMemo(() => {
    const rememberedTournamentId = String(lastJoinedTournamentId || "").trim();
    const scope = ensureArray(registeredTournamentScope);
    const registeredMatches = scope.filter((entry) =>
      isCurrentUserRegisteredForTournament(entry, userIdentitySet)
    );

    if (registeredMatches.length > 0) {
      if (rememberedTournamentId) {
        const rememberedRegistered = registeredMatches.find(
          (entry) => getTournamentId(entry) === rememberedTournamentId
        );
        if (rememberedRegistered) return rememberedRegistered;
      }
      return registeredMatches[0] || null;
    }

    if (!rememberedTournamentId) return null;
    return scope.find((entry) => getTournamentId(entry) === rememberedTournamentId) || null;
  }, [lastJoinedTournamentId, registeredTournamentScope, userIdentitySet]);

  const visibleListTournaments = useMemo(() => {
    const featuredId = getTournamentId(featuredRegisteredTournament);
    if (!featuredId) return visibleTournaments;
    return visibleTournaments.filter((entry) => getTournamentId(entry) !== featuredId);
  }, [featuredRegisteredTournament, visibleTournaments]);

  const openTournament = useCallback(
    async (entry) => {
      const tournamentId = getTournamentId(entry);
      if (!tournamentId) {
        notification.warning({
          message: "Torneo",
          description: i18nT("tournaments.noSeEncontroElIdentificadorDelTorneo"),
        });
        return;
      }

      setOpeningTournamentId(tournamentId);
      const fallbackSequenceNumber = tournamentFallbackSequenceById[tournamentId] || 0;
      const visibleSequenceNumber =
        fallbackSequenceNumber ||
        resolvePublicSequenceNumber(entry);
      let navigationEntry = entry;
      try {
        const response = await axios.get(`/tournaments/${encodeURIComponent(tournamentId)}`);
        const payload = response?.data || {};
        if (payload?.success === false) {
          throw new Error("No se encontro el torneo seleccionado.");
        }

        const hasPasswordFromPayload = Object.prototype.hasOwnProperty.call(
          payload,
          "hasMatchPassword"
        )
          ? parseLooseBoolean(payload.hasMatchPassword)
          : parseLooseBoolean(entry?.hasMatchPassword);

        navigationEntry = {
          ...entry,
          ...payload,
          _id: tournamentId,
          id: tournamentId,
          tournamentName:
            String(payload?.tournamentName || payload?.name || entry?.tournamentName || entry?.name || "")
              .trim() || resolveTournamentName(entry, resolveTournamentMode(entry)),
          startingTime: payload?.startingTime || payload?.start || entry?.startingTime,
          memberSize: Math.max(
            1,
            Number(payload?.memberSize || payload?.members || entry?.memberSize) || 1
          ),
          hasMatchPassword: hasPasswordFromPayload,
          tournamentRewardValue:
            payload?.tournamentRewardValue ?? entry?.tournamentRewardValue ?? 0,
          tournamentUiSequenceNumber:
            visibleSequenceNumber ||
            resolvePublicSequenceNumber(payload),
          tournamentSequenceNumber:
            visibleSequenceNumber ||
            resolvePublicSequenceNumber(payload),
          tournamentNumber:
            visibleSequenceNumber ||
            resolvePublicSequenceNumber(payload),
          displayNumber:
            visibleSequenceNumber ||
            resolvePublicSequenceNumber(payload),
          paidUsers: ensureArray(payload?.paidUsers ?? entry?.paidUsers),
          users: ensureArray(payload?.users ?? entry?.users),
        };
      } catch (error) {
        console.error(error);
        setOpeningTournamentId("");
        notification.warning({
          message: "Torneo",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo validar el torneo antes de abrir la sala.",
        });
        return;
      }

      const sourceKey = isModeInterface
        ? MODE_INTERFACE_SOURCE_BY_ID[activeModeInterface] || "modo-ciencia"
        : "play-tournaments";
      const requestKey = `${sourceKey}-${tournamentId}-${Date.now()}`;
      const tournamentSequenceNumber = resolvePublicSequenceNumber(navigationEntry);
      const query = new URLSearchParams({
        openTournamentWaitingId: tournamentId,
        openTournamentWaitingRequestKey: requestKey,
        openTournamentWaitingReturnTo: `${location.pathname}${location.search || ""}`,
        fromLobbyTournament: "1",
        fromLobbySource: sourceKey,
      });
      navigate(`/game?${query.toString()}`, {
        state: {
          openTournamentWaitingId: tournamentId,
          openTournamentWaitingRecord: navigationEntry,
          openTournamentWaitingRequestKey: requestKey,
          openTournamentWaitingReturnTo: `${location.pathname}${location.search || ""}`,
          openTournamentWaitingAutoPlay: false,
          fromLobbyTournament: "1",
          fromLobbySource: sourceKey,
          source: isModeInterface ? "modeTournaments" : "playTournaments",
          modeInterface: isModeInterface ? activeModeInterface : undefined,
          ...(tournamentSequenceNumber > 0
            ? {
                tournamentUiSequenceNumber: tournamentSequenceNumber,
                tournamentSequenceNumber,
              }
            : {}),
          tournamentMode: isModeInterface ? activeModeInterface : resolveTournamentMode(navigationEntry),
        },
      });
    },
    [
      activeModeInterface,
      isModeInterface,
      location.pathname,
      location.search,
      navigate,
      tournamentFallbackSequenceById,
    ]
  );

  const openChampionship = useCallback(() => {
    navigate("/game?quickView=torneos&tournamentMode=free&fromLobbyTournament=1&fromLobbySource=torneos", {
      state: {
        fromLobbyTournament: "1",
        fromLobbySource: "torneos",
        source: "playTournamentChampionship",
        tournamentSection: "12",
        tournamentMode: "free",
      },
    });
  }, [navigate]);

  const openPlayerProfile = useCallback(
    (row) => {
      if (!row?.playerCode) return;
      navigate(`/game/player/${encodeURIComponent(row.playerCode)}`);
    },
    [navigate]
  );

  const pageTitle = isModeInterface
    ? getTournamentModeLabel(language, activeModeInterface, "routeLabel")
    : tournamentText("playTitle");
  const pageSubtitle = isModeInterface
    ? modeInterfaceCopy.intro
    : tournamentText("playSubtitle");
  const tournamentEmptyLabel = isModeInterface
    ? modeInterfaceCopy.empty
    : tournamentText("noOpenTournaments");

  const renderModeFilterPanel = ({
    icon: Icon,
    title,
    subtitle,
    value,
    options,
    onChange,
    tone,
  }) => (
    <label className={`xima-science-filter-panel xima-science-filter-${tone}`}>
      <span className="xima-science-filter-icon" aria-hidden="true">
        <Icon />
      </span>
      <span className="xima-science-filter-copy">
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
      <span className="xima-science-filter-control">
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => (
            <option key={`${title}-${option.value}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <IoFilterOutline aria-hidden="true" />
      </span>
    </label>
  );

  const renderTournamentRow = (entry, { featured = false } = {}) => {
    const tournamentId = getTournamentId(entry);
    const tournamentSequenceNumber =
      tournamentFallbackSequenceById[tournamentId] ||
      resolvePublicSequenceNumber(entry) ||
      0;
    const tournamentSequenceLabel =
      tournamentSequenceNumber > 0
        ? formatTournamentSequenceLabel(language, tournamentSequenceNumber)
        : "";
    const mode = resolveTournamentMode(entry);
    const theme = MODE_THEME_BY_ID[mode] || MODE_THEME_BY_ID.free;
    const status = getTournamentStatus(entry, nowMs);
    const timing = resolveTournamentTiming(entry, nowMs);
    const memberSize = resolveMemberSize(entry);
    const playerCount = resolvePlayerCount(entry);
    const visiblePlayerCount = resolveVisibleJoinedCount(playerCount);
    const joinedLabel = `${tournamentText("joined")} ${formatTournamentNumber(language, visiblePlayerCount)}`;
    const turnTime = resolveTurnTime(entry);
    const playersBadgeImage = getTournamentPlayersBadgeImage(memberSize);
    const timeBadgeImage = getTournamentTimeBadgeImage(turnTime);
    const prize = resolvePrize(entry);
    const entryFee = resolveEntryFee(entry);
    const hasPassword = parseLooseBoolean(entry?.hasMatchPassword);
    const isWaiting = status === "waiting";
    const opening = openingTournamentId === tournamentId;
    const rowName = localizeTournamentNameForDisplay(
      language,
      entry,
      mode,
      resolveTournamentName(entry, mode)
    );
    const modeLabel = getTournamentModeLabel(language, mode, "routeLabel");
    const playerCountLabel = formatTournamentPlayerCountLabel(language, memberSize);
    const turnTimeLabel = formatTournamentTurnTimeLabel(language, turnTime);
    const versusLabel = formatTournamentVersusLabel(language, memberSize);
    const prizeLabel = formatTournamentPrizeValue(language, prize);
    const editInteractive = hasAdminTournamentAccess && tournamentEditModeEnabled;
    const editSelected = editInteractive && tournamentEditTargetId === tournamentId;
    const handleThumbClick = () => {
      if (editInteractive) {
        openTournamentEditModal(entry);
        return;
      }
      openTournament(entry);
    };

    return (
      <article
        key={`${featured ? "registered" : "tournament"}-${tournamentId}`}
        className={`xima-play-tournaments-row xima-play-tournaments-row-${theme.colorClass}${
          isWaiting ? " xima-play-tournaments-row-waiting" : ""
        }${featured ? " xima-play-tournaments-row-registered" : ""}`}
      >
        <button
          type="button"
          className={`xima-play-tournaments-thumb${
            editInteractive ? " xima-play-tournaments-thumb-editable" : ""
          }${editSelected ? " xima-play-tournaments-thumb-editing" : ""}`}
          onClick={handleThumbClick}
          aria-label={`${editInteractive ? tournamentText("edit") : tournamentText("open")} ${rowName}`}
          title={editInteractive ? tournamentText("editTournament") : undefined}
        >
          <span className="xima-play-tournaments-thumb-icons" aria-hidden="true">
            <img
              src={theme.icon || theme.thumbnail}
              alt=""
              className="xima-play-tournaments-thumb-mode"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
            <img
              src={playersBadgeImage}
              alt=""
              className="xima-play-tournaments-thumb-badge"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
            <img
              src={timeBadgeImage}
              alt=""
              className="xima-play-tournaments-thumb-badge"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </span>
        </button>

        <div className="xima-play-tournaments-info">
          <h3>{rowName}</h3>
          {editInteractive && isOwner ? (
            <button
              type="button"
              className="xima-owner-delete-button"
              disabled={ownerDeletingId === tournamentId}
              onClick={async (event) => {
                event.stopPropagation();
                if (!window.confirm(`¿Eliminar el torneo «${rowName}»? No se puede deshacer.`)) return;
                setOwnerDeletingId(tournamentId);
                try {
                  await axios.post("/tournaments/owner/tournaments/delete", { tournamentId });
                  await loadTournaments(true);
                } catch (error) {
                  window.alert(error?.response?.data?.message || "No se pudo eliminar el torneo.");
                } finally {
                  setOwnerDeletingId("");
                }
              }}
            >
              {ownerDeletingId === tournamentId ? "Eliminando…" : "Eliminar torneo"}
            </button>
          ) : null}
          <p>
            {featured
              ? tournamentText("registeredTournamentDescription")
              : isWaiting
              ? tournamentText("scheduledTournamentDescription")
              : tournamentText("activeTournamentDescription")}
          </p>
          <div className="xima-play-tournaments-meta">
            <span>
              <IoPeopleOutline aria-hidden="true" />
              {playerCountLabel}
            </span>
            <span>
              <IoTrophyOutline aria-hidden="true" />
              {status === "waiting" ? tournamentText("startingSoon") : tournamentText("active")}
            </span>
          </div>
          <div className="xima-play-tournaments-timebox">
            {tournamentSequenceNumber > 0 ? (
              <span
                className="xima-tournament-sequence-chip"
                data-short={formatTournamentSequenceShortLabel(language, tournamentSequenceNumber)}
                title={tournamentSequenceLabel}
              >
                {tournamentSequenceLabel}
              </span>
            ) : null}
            {isWaiting && timing.startRemainingMs > 0 ? (
              <span className="xima-time-chip-waiting">
                <IoTimeOutline aria-hidden="true" />
                {`${tournamentText("starts")} ${formatCountdownLabel(timing.startRemainingMs)}`}
              </span>
            ) : null}
            {!isWaiting && timing.endRemainingMs > 0 ? (
              <span className="xima-time-chip-ending">
                <IoTimeOutline aria-hidden="true" />
                {`${tournamentText("ends")} ${formatCountdownLabel(timing.endRemainingMs)}`}
              </span>
            ) : null}
            {hasPassword ? (
              <span className="xima-play-tournaments-lock-card">
                <IoLockClosedOutline aria-hidden="true" />
                <strong>{tournamentText("withPassword")}</strong>
              </span>
            ) : null}
            <span className="xima-time-chip-joined">
              <IoPeopleOutline aria-hidden="true" />
              {joinedLabel}
            </span>
            {entryFee > 0 ? (
              <span className="xima-time-chip-payment">
                <IoDiamondOutline aria-hidden="true" />
                {tournamentText("payment", {
                  value: formatTournamentNumber(language, entryFee),
                })}
              </span>
            ) : null}
            <span className="xima-time-chip-prize">
              <IoDiamondOutline aria-hidden="true" />
              {prizeLabel}
            </span>
          </div>
        </div>

        <div className="xima-play-tournaments-mode-detail">
          <div
            className="xima-play-tournaments-mode-visuals"
            aria-label={`${modeLabel}, ${playerCountLabel}, ${turnTimeLabel}`}
          >
            <img
              src={theme.icon || theme.thumbnail}
              alt=""
              aria-hidden="true"
              className="xima-play-tournaments-mode-logo"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
            <img
              src={playersBadgeImage}
              alt={playerCountLabel}
              className="xima-play-tournaments-feature-badge"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
            <img
              src={timeBadgeImage}
              alt={turnTimeLabel}
              className="xima-play-tournaments-feature-badge"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </div>
          <strong>{modeLabel}</strong>
          <span>
            <IoPeopleOutline aria-hidden="true" />
            {versusLabel}
          </span>
          <span>
            <IoTimeOutline aria-hidden="true" />
            {turnTimeLabel}
          </span>
        </div>

        <div className="xima-play-tournaments-prize">
          <span>{tournamentText("prize")}</span>
          <strong>
            <IoDiamondOutline aria-hidden="true" />
            {prizeLabel}
          </strong>
          <button
            type="button"
            onClick={() => openTournament(entry)}
            className="xima-play-tournaments-join"
            disabled={opening}
          >
            {opening ? tournamentText("opening") : featured ? tournamentText("enter") : tournamentText("join")}
          </button>
        </div>
      </article>
    );
  };

  return (
    <>
      <main
        className={`xima-play-tournaments-screen${
          isModeInterface
            ? ` xima-mode-interface-screen xima-mode-interface-${modeInterfaceTheme.colorClass}`
            : ""
        }`}
      >
      <div className="xima-play-tournaments-overlay" aria-hidden="true" />
      <div className="xima-play-tournaments-shell">
        <header className="xima-play-tournaments-header">
          <div className="xima-play-tournaments-logo" aria-label="XIMA">
            XiMA
          </div>
          <button
            type="button"
            onClick={() => navigate("/game/lobby")}
            className="xima-play-tournaments-back"
          >
            <span aria-hidden="true">&lt;</span>
            {tournamentText("backToLobby")}
          </button>
          <div className="xima-play-tournaments-title">
            <h1
              className={
                isModeInterface && activeModeInterface === "sciencevsazar"
                  ? "xima-play-tournaments-title-split"
                  : ""
              }
            >
              {isModeInterface && activeModeInterface === "sciencevsazar" ? (
                <>
                  <span>{tournamentText("mode")}</span>
                  <span>{getTournamentModeLabel(language, "sciencevsazar", "shortLabel")}</span>
                </>
              ) : (
                pageTitle
              )}
            </h1>
            <p>{pageSubtitle}</p>
          </div>
        </header>

        <section
          className="xima-play-tournaments-hero"
          aria-label={
            isModeInterface
              ? `${tournamentText("filterByMode")} ${modeInterfaceCopy.routeLabel}`
              : tournamentText("filterByMode")
          }
        >
          <div className="xima-play-tournaments-hero-art" aria-hidden="true">
            <img
              src="/assets/images/torneo-button.png"
              alt=""
              className="xima-play-tournaments-hero-board"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </div>
          <div className="xima-play-tournaments-mode-grid">
            {isModeInterface ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setModePlayerFilter("all");
                    setModeTimeFilter("all");
                  }}
                  className={`xima-play-tournaments-mode-card ${modeInterfaceOption.cardClass} xima-play-tournaments-selected`}
                  aria-pressed="true"
                >
                  <span className="xima-play-tournaments-mode-icon">
                    <img
                      src={modeInterfaceOption.icon}
                      alt=""
                      aria-hidden="true"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  </span>
                  <span className="xima-play-tournaments-mode-copy">
                    <strong>{getTournamentModeLabel(language, activeModeInterface, "routeLabel")}</strong>
                    <small>{modeInterfaceCopy.onlyLabel}</small>
                    <small>{modeInterfaceCopy.detail}</small>
                  </span>
                </button>

                {renderModeFilterPanel({
                  icon: IoPeopleOutline,
                  title: tournamentText("filterPlayers"),
                  subtitle: tournamentText("filterPlayersShortDescription"),
                  value: modePlayerFilter,
                  options: modePlayerOptions,
                  onChange: setModePlayerFilter,
                  tone: "players",
                })}

                {renderModeFilterPanel({
                  icon: IoTimeOutline,
                  title: tournamentText("filterTime"),
                  subtitle: tournamentText("filterTimeShortDescription"),
                  value: modeTimeFilter,
                  options: modeTimeOptions,
                  onChange: setModeTimeFilter,
                  tone: "time",
                })}
              </>
            ) : (
              MODE_OPTIONS.map((option) => {
                const selected = modeFilter === option.id;
                const modeCopy = getModeInterfaceCopy(language, option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      setModeFilter(option.id);
                    }}
                    className={`xima-play-tournaments-mode-card ${option.cardClass}${
                      selected ? " xima-play-tournaments-selected" : ""
                    }`}
                    aria-pressed={selected}
                  >
                    <span className="xima-play-tournaments-mode-icon">
                      <img
                        src={option.icon}
                        alt=""
                        aria-hidden="true"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    </span>
                    <span className="xima-play-tournaments-mode-copy">
                      <strong>{modeCopy.routeLabel}</strong>
                      <small>{modeCopy.cardSubtitle}</small>
                      {modeCopy.cardDetail ? <small>{modeCopy.cardDetail}</small> : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </section>

        <section className="xima-play-tournaments-layout">
          <div className="xima-play-tournaments-main-panel">
            <div className="xima-play-tournaments-panel-head">
              <div className="xima-play-tournaments-head-icon" aria-hidden="true">
                <IoTrophyOutline />
              </div>
              <div>
                <h2>{tournamentText("openTournaments")}</h2>
                <p>
                  {isModeInterface
                    ? modeInterfaceCopy.panelDescription
                    : tournamentText("openTournamentsDescription")}
                </p>
              </div>
              <div className="xima-play-tournaments-filters">
                {hasAdminTournamentAccess ? (
                  <button
                    type="button"
                    onClick={handleToggleTournamentEditMode}
                    className={`xima-play-tournaments-edit-toggle${
                      tournamentEditModeEnabled ? " is-active" : ""
                    }`}
                  >
                    {tournamentEditModeEnabled
                      ? tournamentText("editTournamentOn")
                      : tournamentText("editTournament")}
                  </button>
                ) : null}
                {isModeInterface ? (
                  <label>
                    <span>{tournamentText("mode")}</span>
                    <span className="xima-play-tournaments-select-wrap xima-science-fixed-filter">
                      <img
                        src={modeInterfaceTheme.icon}
                        alt=""
                        aria-hidden="true"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                      <strong>{modeInterfaceCopy.shortLabel}</strong>
                    </span>
                  </label>
                ) : (
                  <label>
                    <span>{tournamentText("filterByMode")}</span>
                    <span className="xima-play-tournaments-select-wrap">
                      <select
                        value={modeFilter}
                        onChange={(event) => {
                          setModeFilter(event.target.value);
                        }}
                      >
                        {filterOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <IoFilterOutline aria-hidden="true" />
                    </span>
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => loadTournaments(false)}
                  className="xima-play-tournaments-refresh"
                  disabled={loading}
                >
                  <IoRefreshOutline aria-hidden="true" />
                </button>
              </div>
            </div>

            <div
              className={`xima-play-tournaments-list${
                featuredRegisteredTournament ? " xima-play-tournaments-list-with-registered" : ""
              }`}
            >
              {featuredRegisteredTournament ? (
                <div className="xima-play-tournaments-registered-panel">
                  <div className="xima-play-tournaments-registered-title">
                    <IoCheckmarkCircleOutline aria-hidden="true" />
                    <span>{tournamentText("registered")}</span>
                  </div>
                  {renderTournamentRow(featuredRegisteredTournament, { featured: true })}
                </div>
              ) : null}
              <div className="xima-play-tournaments-scroll-list">
                {loading && visibleListTournaments.length === 0 && !featuredRegisteredTournament ? (
                  <div className="xima-play-tournaments-empty">{tournamentText("loadingTournaments")}</div>
                ) : visibleListTournaments.length > 0 ? (
                  visibleListTournaments.map((entry) => renderTournamentRow(entry))
                ) : (
                  !featuredRegisteredTournament ? (
                    <div className="xima-play-tournaments-empty">
                      {tournamentEmptyLabel}
                    </div>
                  ) : (
                    <div className="xima-play-tournaments-empty">
                      {tournamentText("noMoreOpenTournaments")}
                    </div>
                  )
                )}
              </div>
            </div>

          </div>

          <aside className="xima-play-tournaments-side">
            <section className="xima-play-tournaments-guide">
              <h2>{tournamentText("howItWorks")}</h2>
              {isModeInterface ? (
                <>
                  <div className="xima-play-tournaments-guide-step">
                    <img
                      src={modeInterfaceTheme.icon}
                      alt=""
                      aria-hidden="true"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                    <div>
                      <strong>{modeInterfaceCopy.routeLabel}</strong>
                      <span>{modeInterfaceCopy.guideMode}</span>
                    </div>
                  </div>
                  <div className="xima-play-tournaments-guide-step">
                    <IoPeopleOutline aria-hidden="true" />
                    <div>
                      <strong>{tournamentText("filterPlayers")}</strong>
                      <span>{tournamentText("filterPlayersDescription")}</span>
                    </div>
                  </div>
                  <div className="xima-play-tournaments-guide-step">
                    <IoTimeOutline aria-hidden="true" />
                    <div>
                      <strong>{tournamentText("filterTime")}</strong>
                      <span>{tournamentText("filterTimeDescription")}</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="xima-play-tournaments-guide-step">
                    <img
                      src="/mode-icons/free.png"
                      alt=""
                      aria-hidden="true"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                    <div>
                      <strong>{tournamentText("chooseMode")}</strong>
                      <span>{tournamentText("chooseModeDescription")}</span>
                    </div>
                  </div>
                  <div className="xima-play-tournaments-guide-step">
                    <img
                      src="/mode-icons/logo-modo-azar.png"
                      alt=""
                      aria-hidden="true"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                    <div>
                      <strong>{tournamentText("joinTournamentGuide")}</strong>
                      <span>{tournamentText("joinTournamentGuideDescription")}</span>
                    </div>
                  </div>
                  <div className="xima-play-tournaments-guide-step">
                    <IoTrophyOutline aria-hidden="true" />
                    <div>
                      <strong>{tournamentText("competeAndWin")}</strong>
                      <span>{tournamentText("competeAndWinDescription")}</span>
                    </div>
                  </div>
                </>
              )}
            </section>

            <section className="xima-play-tournaments-ranking">
              <div className="xima-play-tournaments-ranking-head">
                <h2>{tournamentText("topPlayers")}</h2>
                <button type="button" onClick={() => navigate("/game/ranking")} aria-label={tournamentText("viewRankingAria")}>
                  <IoChevronForward aria-hidden="true" />
                </button>
              </div>
              <div className="xima-play-tournaments-ranking-list">
                {leaderboardRows.length > 0 ? (
                  leaderboardRows.map((row) => (
                    <button
                      key={`${row.rank}-${row.name}-${row.playerCode}`}
                      type="button"
                      onClick={() => openPlayerProfile(row)}
                      className="xima-play-tournaments-ranking-row"
                    >
                      <span>{row.rank}</span>
                      <strong>{row.name}</strong>
                      <em>{row.score}</em>
                    </button>
                  ))
                ) : (
                  <div className="xima-play-tournaments-ranking-empty">{tournamentText("noRankingAvailable")}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => navigate("/game/ranking")}
                className="xima-play-tournaments-ranking-link"
              >
                <IoTrophyOutline aria-hidden="true" />
                {tournamentText("viewFullRanking")}
              </button>
            </section>

            <section className="xima-play-tournaments-actions">
              <button
                type="button"
                onClick={() => navigate("/game/store?fromLobbySource=play-tournaments")}
                className="xima-play-tournaments-action-card xima-play-tournaments-store"
              >
                <IoCartOutline aria-hidden="true" />
                <span>
                  <strong>{tournamentText("goToShop")}</strong>
                  <small>{tournamentText("shopDescription")}</small>
                </span>
                <img
                  src="/store/xima-board/xima-board-0.png"
                  alt=""
                  aria-hidden="true"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              </button>

              <button
                type="button"
                onClick={openChampionship}
                className="xima-play-tournaments-action-card xima-play-tournaments-championship"
              >
                <IoRocketOutline aria-hidden="true" />
                <span>
                  <strong>{tournamentText("championshipTitle")}</strong>
                  <small>{tournamentText("championshipDescription")}</small>
                </span>
                {isModeInterface ? null : (
                  <img
                    src="/assets/images/podioconbloquemorado36.png"
                    alt=""
                    aria-hidden="true"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                )}
              </button>
            </section>
          </aside>
        </section>
      </div>
      </main>

      <Modal
        open={tournamentEditModalOpen}
        onCancel={closeTournamentEditModal}
        onOk={handleSaveTournamentEdit}
        okText={tournamentText("saveChanges")}
        cancelText={tournamentText("cancel")}
        confirmLoading={tournamentEditSaving}
        okButtonProps={{ danger: true }}
        cancelButtonProps={{ disabled: tournamentEditSaving }}
        maskClosable={!tournamentEditSaving}
        title={<span className="xima-tournament-edit-title">{tournamentText("editTournament")}</span>}
        className="xima-tournament-edit-modal"
      >
        <div className="xima-tournament-edit-form">
          <div className="xima-tournament-edit-selected">
            <span>{tournamentText("selectedTournament")}</span>
            <strong>
              {String(tournamentEditTargetLabel || tournamentText("tournamentFallback"))}
            </strong>
            <small>ID: {String(tournamentEditTargetId || tournamentText("missingId"))}</small>
          </div>

          <div className="xima-tournament-edit-field">
            <span>{tournamentText("tournamentPassword")}</span>
            <div className="xima-tournament-edit-options">
              <button
                type="button"
                disabled={tournamentEditSaving}
                onClick={() => setTournamentEditMatchPasswordEnabledDraft(true)}
                className={tournamentEditMatchPasswordEnabledDraft ? "is-selected" : ""}
              >
                {tournamentText("private")}
              </button>
              <button
                type="button"
                disabled={tournamentEditSaving}
                onClick={() => {
                  setTournamentEditMatchPasswordEnabledDraft(false);
                  setTournamentEditMatchPasswordDraft("");
                }}
                className={!tournamentEditMatchPasswordEnabledDraft ? "is-selected" : ""}
              >
                {tournamentText("public")}
              </button>
            </div>
            {tournamentEditMatchPasswordEnabledDraft ? (
              <Input.Password
                value={tournamentEditMatchPasswordDraft}
                maxLength={MATCH_PASSWORD_MAX_LENGTH}
                disabled={tournamentEditSaving}
                onChange={(event) =>
                  setTournamentEditMatchPasswordDraft(
                    normalizeMatchPassword(event?.target?.value || "")
                  )
                }
                placeholder={
                  tournamentEditMatchPasswordConfigured
                    ? tournamentText("optionalNewPassword")
                    : tournamentText("requiredNewPassword")
                }
              />
            ) : null}
            {!tournamentEditMatchPasswordConfigured &&
            tournamentEditMatchPasswordEnabledDraft ? (
              <small>{tournamentText("repairPrivateTournamentPassword")}</small>
            ) : null}
          </div>

          <label className="xima-tournament-edit-field">
            <span>{tournamentText("tournamentName")}</span>
            <Input
              value={tournamentEditNameDraft}
              maxLength={120}
              disabled={tournamentEditSaving}
              onChange={(event) =>
                setTournamentEditNameDraft(String(event?.target?.value || "").slice(0, 120))
              }
              placeholder={tournamentText("specialTournamentPlaceholder")}
            />
          </label>

          <div className="xima-tournament-edit-grid">
            <label className="xima-tournament-edit-field">
              <span>{tournamentText("startDate")}</span>
              <input
                type="date"
                value={tournamentEditStartDateDraft}
                disabled={tournamentEditSaving}
                onChange={(event) =>
                  setTournamentEditStartDateDraft(String(event?.target?.value || ""))
                }
              />
            </label>
            <label className="xima-tournament-edit-field">
              <span>{tournamentText("startTime")}</span>
              <input
                type="time"
                value={tournamentEditStartTimeDraft}
                disabled={tournamentEditSaving}
                onChange={(event) =>
                  setTournamentEditStartTimeDraft(String(event?.target?.value || ""))
                }
              />
            </label>
          </div>

          <div className="xima-tournament-edit-grid">
            <label className="xima-tournament-edit-field">
              <span>{tournamentText("durationDays")}</span>
              <InputNumber
                min={1}
                max={3650}
                precision={0}
                value={tournamentEditDurationDraft}
                disabled={tournamentEditSaving}
                onChange={(value) => setTournamentEditDurationDraft(value)}
                className="xima-tournament-edit-number"
              />
            </label>
            <label className="xima-tournament-edit-field">
              <span>{tournamentText("rewardX")}</span>
              <InputNumber
                min={0}
                max={MAX_TOURNAMENT_REWARD_VALUE}
                precision={0}
                value={tournamentEditRewardValueDraft}
                disabled={tournamentEditSaving}
                onChange={(value) => setTournamentEditRewardValueDraft(value)}
                className="xima-tournament-edit-number"
              />
            </label>
            <label className="xima-tournament-edit-field">
              <span>{tournamentText("winnerPrizeX")}</span>
              <InputNumber
                min={0}
                max={MAX_TOURNAMENT_REWARD_VALUE}
                precision={0}
                value={tournamentEditWinnerPrizeValueDraft}
                disabled={tournamentEditSaving}
                onChange={(value) => setTournamentEditWinnerPrizeValueDraft(value)}
                className="xima-tournament-edit-number"
              />
            </label>
          </div>

          <div className="xima-tournament-edit-field">
            <span>{tournamentText("timePerTurn")}</span>
            <div className="xima-tournament-edit-options">
              {TOURNAMENT_EDIT_HAVING_TIME_OPTIONS.map((value) => (
                <button
                  key={`edit-tournament-time-${value}`}
                  type="button"
                  disabled={tournamentEditSaving}
                  onClick={() => setTournamentEditHavingTimeDraft(value)}
                  className={Number(tournamentEditHavingTimeDraft) === value ? "is-selected" : ""}
                >
                  {String(value).padStart(2, "0")}:00
                </button>
              ))}
            </div>
          </div>

          <div className="xima-tournament-edit-field">
            <span>{tournamentText("playerQuantity")}</span>
            <div className="xima-tournament-edit-options xima-tournament-edit-player-options">
              {MEMBER_OPTIONS.map((value) => (
                <button
                  key={`edit-tournament-member-${value}`}
                  type="button"
                  disabled={tournamentEditSaving}
                  onClick={() => setTournamentEditMemberSizeDraft(value)}
                  className={Number(tournamentEditMemberSizeDraft) === value ? "is-selected" : ""}
                >
                  {formatTournamentVersusLabel(language, value)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PlayTournaments;
